import React from "react";
import axios from 'axios';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import QuestionFilterComponent from "./QuestionFilterComponent"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import SchoolIcon from "@mui/icons-material/School";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import TopAreasPanel from "./TopAreasPanel";
import TuneIcon from '@mui/icons-material/Tune';
import KeyInfo from "./KeyInfo";
import {
  Box, Typography, IconButton, Select, MenuItem, ListSubheader, Paper, Grid, Stack,
  Button, Accordion, AccordionSummary, AccordionDetails, Collapse, LinearProgress,
  Table, TableHead, Tooltip, TableBody, TableRow, TableCell, ButtonBase,
  ToggleButtonGroup, ToggleButton, CircularProgress, TextField
} from "@mui/material";
import AreaSearch from "./AreaSearch"
import { stateFipsToName } from "./utils/idUtils";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Cell,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";

// Minimum number of visible questions before the question list is sectioned by
// wave coverage ("Tracking questions" / "Wave N questions"). That sectioning
// exists for the ACC configs, which carry 42 metrics across three wave batches
// and are genuinely hard to scan unsectioned. On a short list it is pure noise:
// a two-question client config would otherwise get two headers for two rows.
// Configs with explicit `subgroup` labels bypass wave sectioning entirely and
// are unaffected by this.
const MIN_VARS_FOR_WAVE_SECTIONS = 6;

const STATE_NAME_TO_ABBR = {
  "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA",
  "Colorado":"CO","Connecticut":"CT","Delaware":"DE","Florida":"FL","Georgia":"GA",
  "Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA",
  "Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD",
  "Massachusetts":"MA","Michigan":"MI","Minnesota":"MN","Mississippi":"MS",
  "Missouri":"MO","Montana":"MT","Nebraska":"NE","Nevada":"NV","New Hampshire":"NH",
  "New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC",
  "North Dakota":"ND","Ohio":"OH","Oklahoma":"OK","Oregon":"OR","Pennsylvania":"PA",
  "Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD","Tennessee":"TN",
  "Texas":"TX","Utah":"UT","Vermont":"VT","Virginia":"VA","Washington":"WA",
  "West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY","District of Columbia":"DC",
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg: "#ffffff",
  surface: "#f8f9fc",
  surfaceHover: "#f0f2f7",
  border: "rgba(15,23,42,0.07)",
  borderStrong: "rgba(15,23,42,0.13)",
  text: "#0f172a",
  textMuted: "#64748b",
  textFaint: "rgb(128,128,128)",
  accent: "#0f9fab",
  accentDim: "rgba(15,159,171,0.08)",
  accentGlow: "rgba(15,159,171,0.04)",
  activeTab: "#e8fbfb",
  chip: "#f3f4f6",
  chipActive: "rgba(15,159,171,0.1)",
  chipActiveBorder: "rgba(15,159,171,0.3)",
  pillBg: "rgba(0,0,0,0.03)",
  gradient: "linear-gradient(135deg, #edfffe 0%, #ffffff 100%)",
};

const SIDEBAR_WIDTH = 450;

// ─── Shared sub-components ────────────────────────────────────────────────────
const SectionDivider = () => (
  <Box sx={{ height: "1px", bgcolor: T.border, mx: 2.5 }} />
);
const FilterChip = ({ label, active, onClick, disabled = false }) => (
  <ButtonBase
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: "6px",
      border: "1px solid",
      borderColor: disabled
        ? T.border
        : active
          ? T.chipActiveBorder
          : T.border,
      bgcolor: disabled
        ? "#f3f4f6"
        : active
          ? T.chipActive
          : T.chip,
      color: disabled
        ? "#9ca3af"
        : active
          ? T.accent
          : T.textMuted,
      fontSize: "11px",
      fontWeight: active && !disabled ? 600 : 400,
      fontFamily: "'DM Sans', sans-serif",
      letterSpacing: "0.02em",
      transition: "all 0.15s ease",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.7 : 1,
      "&:hover": disabled
        ? {}
        : {
            borderColor: active ? T.chipActiveBorder : T.borderStrong,
            bgcolor: active ? T.chipActive : T.surfaceHover,
            color: active ? T.accent : T.text,
          },
    }}
  >
    {label}
  </ButtonBase>
);

const DemographicGroup = ({ icon: Icon, label, iconColor, children }) => (
  <Box>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
      <Icon sx={{ fontSize: 13, color: iconColor || T.textFaint }} />
      <Typography sx={{ fontSize: "10px", fontWeight: 600, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif" }}>
        {label}
      </Typography>
    </Stack>
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {children}
    </Stack>
  </Box>
);

const StyledAccordion = ({ children, defaultExpanded, ...props }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    elevation={0}
    disableGutters
    {...props}
    sx={{
      bgcolor: "transparent",
      border: "none",
      borderBottom: `1px solid ${T.border}`,
      borderRadius: "0 !important",
      "&:before": { display: "none" },
      "&.Mui-expanded": { margin: 0 },
      ...props.sx,
    }}
  >
    {children}
  </Accordion>
);

const StyledAccordionSummary = ({ children, ...props }) => (
  <AccordionSummary
    expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: T.textFaint }} />}
    {...props}
    sx={{
      px: 2.5,
      py: 0,
      minHeight: "44px !important",
      "&.Mui-expanded": { minHeight: "44px !important" },
      "& .MuiAccordionSummary-content": { my: "0 !important", alignItems: "center" },
      "&:hover": { bgcolor: T.accentGlow },
      transition: "background 0.15s ease",
    }}
  >
    {children}
  </AccordionSummary>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: "11px",
    fontWeight: 700,
    color: T.textFaint,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    {children}
  </Typography>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Sidebar = ({
  isPanelOpen = true,
  togglePanel = () => {},
  sectionTitle = "Explore",
  blurbText = "",
  selectedAreaLabel,
  groupsById,
  selectedVariable,
  onVariableChange,
  genderFilter,
  onGenderChange,
  ageFilter,
  onAgeChange,
  ethnicityFilter,
  onEthnicityChange,
  partyFilter,
  onPartyChange,
  educationFilter,
  onEducationChange,
  vote24Filter,
  onVote24Change,
  preferenceFilter,
  onPreferenceFilterChange,
  Datablurb,
  selectedFeatureData,
  onClearAll,
  sidebarData,
  theme_colours,
  selectedStateName,
  selectedFeatureLevel,
  ActionTargetVariables,
  onActionTargetVariableChange,
  ActionTargetSubVariables,
  handleActionTargetSubVariableChange,
  ActionTargetRanges,
  ActionTargetMAXRanges,
  handleActionTargetRangesChange,
  ActionTargetConcentration,
  handleActionTargetConcentrationChange,
  audienceInsight,
  audienceInsightLoading,
  onAudienceInsightRequest,
  ActionMode,
  handleActionModeChange,
  reportState,
  reportDistrict,
  onReportStateChange,
  onReportDistrictChange,
  selectedDemographics,
  rankedItems,
  onAreaSelect,
  hoverArea,
  poliInfo,
  geoLevel,
  selectedAreaId,
  selectedRowMatchIds,
  selectedFeature,
  poliInfoToplevel,
  colorLabels,
  colorMap,
  selectedTheme,
  compareWaves,
  waveSelect,
  onSidebarHoverChange,
  mapLoading,
  demo,
  showChatTab,
  chatContext,
  onHighlightAreas,
  chatHighlightCount,
  onClearHighlights,
}) => {
  const isSel = (val, target) => val === target;
  const toggleVal = (curr, next) => (curr === next ? null : next);
  const [detailsOpen] = React.useState(true);
  const [compareChartMode, setCompareChartMode] = React.useState("bars");
  const effectiveChartMode = compareChartMode;
  const [activeWaveKeys, setActiveWaveKeys] = React.useState(null);
  const [hoveredTrendKey, setHoveredTrendKey] = React.useState(null);
  const [showNationalLine, setShowNationalLine] = React.useState(false);

  // ── Chat tab state ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = React.useState("data");
  const [chatMessages, setChatMessages] = React.useState([
    { role: 'assistant', content: 'Ask me about the survey data — things like "Show districts where healthcare support is highest" or "What are the top issues in California?"', queryUsed: null },
  ]);
  const [chatInput, setChatInput] = React.useState('');
  const [chatLoading, setChatLoading] = React.useState(false);
  const [chatLastInput, setChatLastInput] = React.useState('');
  const chatBottomRef = React.useRef(null);
  const [chatQueryOpen, setChatQueryOpen] = React.useState(null); // index of expanded query pill

  // Keep a stable reference to the last waveSeries that had state data, so async partial
  // updates (national-only) don't cause the state line to disappear mid-render.
  const lastGoodWaveSeriesRef = React.useRef([]);
  const lastGoodWaveSeriesKeyRef = React.useRef(null);

  const selected = selectedVariable ? groupsById?.[selectedVariable] : null;

  React.useEffect(() => {
    const g = groupsById?.[selectedVariable];
    if (selectedVariable && g?.type === "categorical" && !preferenceFilter) {
      onPreferenceFilterChange?.("top");
    }
  }, [selectedVariable, groupsById, preferenceFilter, onPreferenceFilterChange]);

  React.useEffect(() => {
    if (compareWaves) {
      setCompareChartMode("trend");
    } else {
      setCompareChartMode("bars");
    }
  }, [compareWaves, selectedAreaLabel, selectedVariable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset wave key toggles when the variable changes
  React.useEffect(() => {
    setActiveWaveKeys(null);
    setHoveredTrendKey(null);
  }, [selectedVariable]);

  // Scroll chat to bottom on new messages
  React.useEffect(() => {
    if (activeTab === 'chat' && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  const handleChatSend = async (retryText) => {
    const text = (retryText || chatInput).trim();
    if (!text || chatLoading) return;
    const userMsg = { role: 'user', content: text, queryUsed: null };
    const nextMessages = retryText ? chatMessages : [...chatMessages, userMsg];
    if (!retryText) {
      setChatMessages(nextMessages);
      setChatInput('');
    }
    setChatLastInput(text);
    setChatLoading(true);
    const history = nextMessages.slice(1).slice(-8).map(m => ({ role: m.role, content: m.content }));
    try {
      const enrichedContext = { ...(chatContext || {}) };
      const response = await axios.post('/api/chat/', { message: text, context: enrichedContext, history }, { withCredentials: true });
      const data = response.data;
      const assistantMsg = {
        role: 'assistant',
        content: data.answer || data.error || 'No response.',
        queryUsed: data.out_of_scope ? null : data.query_used,
        sampleNote: data.sample_note || null,
        isError: !!data.error,
      };
      setChatMessages(prev => retryText
        ? [...prev.slice(0, -1), assistantMsg]
        : [...prev, assistantMsg]
      );
      if (!data.out_of_scope && !data.error && onHighlightAreas) {
        onHighlightAreas(data.highlight_areas || [], data.query_used);
      }
    } catch (err) {
      const status = err.response?.status;
      const errMsg = status === 403
        ? 'Chat is not available for your account.'
        : status === 429
          ? 'Too many requests — please wait a moment and try again.'
          : status === 502 || status === 503
            ? 'The AI service is temporarily unavailable. Please try again shortly.'
            : 'Something went wrong. Please try again.';
      setChatMessages(prev => [...prev, { role: 'assistant', content: errMsg, queryUsed: null, isError: true, retryText: text }]);
    } finally {
      setChatLoading(false);
    }
  };

  const group = groupsById?.[selectedVariable];
  const labels = colorLabels || {};
  const colors = colorMap || {};
  const byLabel = new Map();

  const upsert = (arr, field) => {
    (arr || []).forEach(({ key, label, value }) => {
      const k = key || Object.keys(labels).find((lk) => labels[lk] === label);
      const displayLabel = labels[k] || label || "(no label)";
      const row = byLabel.get(displayLabel) || {
        label: displayLabel,
        national: null,
        state: null,
        selected: null,
        unfilteredNational: null,
        unfilteredState: null,
        unfilteredSelected: null,
        color: "#4f8ef7",
      };
      const num = Number(value);
      row[field] = Number.isFinite(num) ? num : null;
      if (k && colors[k]) row.color = colors[k];
      byLabel.set(displayLabel, row);
    });
  };

  upsert(selectedFeatureData?.national, "national");
  upsert(selectedFeatureData?.state, "state");
  upsert(selectedFeatureData?.selected, "selected");
  upsert(selectedFeatureData?.unfilteredNational, "unfilteredNational");
  upsert(selectedFeatureData?.unfilteredState, "unfilteredState");
  upsert(selectedFeatureData?.unfilteredSelected, "unfilteredSelected");

  const combined = Array.from(byLabel.values()).sort((a, b) => {
    const an = Number.isFinite(a.national) ? a.national : -Infinity;
    const bn = Number.isFinite(b.national) ? b.national : -Infinity;
    if (bn !== an) return bn - an;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });

  const seriesAlmostEqual = (rows, a, b, tol = 0.01) => {
    let compared = false;
    for (const r of rows) {
      const va = r[a], vb = r[b];
      if (va == null && vb == null) continue;
      if (va == null || vb == null) return false;
      compared = true;
      if (Math.abs(va - vb) > tol) return false;
    }
    return compared;
  };

  const seriesAllNullishOrZero = (rows, key) =>
    rows.every((r) => r[key] == null || !Number.isFinite(r[key]) || Math.abs(r[key]) < 1e-9);

  const selectedIsState = typeof selectedFeatureLevel === "string" && selectedFeatureLevel === "states";
  const hideSelected = selectedIsState
    || seriesAlmostEqual(combined, "state", "selected", 0.01)
    || seriesAllNullishOrZero(combined, "selected");

  const hasNational = combined.some((d) => Number.isFinite(d.national));
  const hasState = combined.some((d) => Number.isFinite(d.state));
  const hasSelected = !hideSelected && combined.some((d) => Number.isFinite(d.selected));

  const stateAbbr = selectedStateName ? (STATE_NAME_TO_ABBR[selectedStateName] || selectedStateName) : null;
  const stateLabel = stateAbbr || selectedStateName || "State";
  const selectedLabel = selectedAreaLabel || "Selected";
  const targetScopeLabel = selectedFeature?.name || selectedStateName || "National";

  // ── Demographic compare mode ──────────────────────────────────────────────
  const hasDemoFilter = selectedDemographics &&
    Object.values(selectedDemographics).some((v) => Array.isArray(v) ? v.length : Boolean(v));
  const demoFilterLabel = (() => {
    if (!selectedDemographics) return null;
    const parts = Object.values(selectedDemographics).filter(Boolean).flatMap(v => Array.isArray(v) ? v : [v]);
    return parts.length ? parts.join(", ") : null;
  })();
  const unfilteredLocalKey = (() => {
    if (combined.some((d) => Number.isFinite(d.unfilteredSelected))) return "unfilteredSelected";
    if (combined.some((d) => Number.isFinite(d.unfilteredState))) return "unfilteredState";
    if (combined.some((d) => Number.isFinite(d.unfilteredNational))) return "unfilteredNational";
    return null;
  })();
  const filteredLocalKey = hasSelected ? "selected" : hasState ? "state" : hasNational ? "national" : null;
  const unfilteredPending = hasDemoFilter && !unfilteredLocalKey && filteredLocalKey;
  const isDemoCompareMode = (hasDemoFilter && unfilteredLocalKey && filteredLocalKey) || unfilteredPending;

  const measureTextWidth = React.useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    return (text, font = "12px DM Sans, sans-serif") => {
      if (!ctx) return 120;
      ctx.font = font;
      return ctx.measureText(String(text ?? "")).width;
    };
  }, []);

  const yAxisWidth = React.useMemo(() => {
    const pad = 10;
    const minW = 50;
    const maxW = 180;
    const longest = (combined || []).reduce((m, d) => {
      const w = measureTextWidth(d.label);
      return Math.max(m, w);
    }, 0);
    return Math.min(maxW, Math.max(minW, Math.ceil(longest) + pad));
  }, [combined, measureTextWidth]);

  const getSortVal = (row) => {
    if (hasSelected && Number.isFinite(row.selected)) return row.selected;
    if (hasState && Number.isFinite(row.state)) return row.state;
    if (hasNational && Number.isFinite(row.national)) return row.national;
    return -Infinity;
  };

  const sortedData = React.useMemo(
    () => [...combined].sort((a, b) => getSortVal(b) - getSortVal(a)),
    [combined]
  );

  const baseHeight = 240;
  const perRow = sortedData.length > 15 ? 30 : 26;
  const chartHeight = Math.max(baseHeight, perRow * sortedData.length + 40);

  const TickWrap = React.useCallback(({ x, y, payload }) => {
    const text = String(payload?.value ?? "");
    const maxLineWidth = Math.min(yAxisWidth - 12, 160);
    const words = text.split(" ");
    const lines = [];
    let line = [];
    const lineWidth = (arr) => measureTextWidth(arr.join(" "));
    words.forEach((w) => {
      const test = [...line, w];
      if (lineWidth(test) > maxLineWidth && line.length) {
        lines.push(line.join(" "));
        line = [w];
      } else {
        line = test;
      }
    });
    if (line.length) lines.push(line.join(" "));
    return (
      <text x={x - 5} y={y} textAnchor="end" fontSize={11} fill={T.textMuted} style={{ pointerEvents: "none", fontFamily: "'DM Sans', sans-serif" }}>
        {lines.map((t, i) => (
          <tspan key={i} x={x - 5} dy={i === 0 ? 0 : 12}>{t}</tspan>
        ))}
      </text>
    );
  }, [measureTextWidth, yAxisWidth]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "row", zIndex: 1200 }}>

      {/* ── Main Panel ── */}
      <Box
        id="tour-sidebar"
        onMouseEnter={() => onSidebarHoverChange?.(true)}
        onMouseLeave={() => onSidebarHoverChange?.(false)}
        sx={{
          width: isPanelOpen ? `${SIDEBAR_WIDTH}px` : "0px",
          height: "calc(100vh - 64px)",
          bgcolor: T.bg,
          boxShadow: isPanelOpen ? "4px 0 20px rgba(0,0,0,0.08)" : "none",
          transition: "width 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          opacity: isPanelOpen ? 1 : 0,
          pointerEvents: isPanelOpen ? "auto" : "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "'DM Sans', sans-serif",
          "@import": "url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap')",
        }}
      >

        {/* ── Sticky Header ── */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: T.bg,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          {/* Logo / Title */}
          {(() => {
            const logoThemes = ["acc_politics", "acc_climate", "acc_policy"];
            if (logoThemes.includes(selectedTheme)) {
              return (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1.5, px: 3 }}>
                  <Typography sx={{ fontWeight: 700, color: T.text, mr: 2, lineHeight: 1, fontFamily: "'DM Sans', sans-serif", fontSize: "15px", letterSpacing: "-0.01em" }}>
                    {sectionTitle}
                  </Typography>
                  <img
                    src={`${process.env.PUBLIC_URL}/static/images/acc.png`}
                    alt="ACC Logo"
                    style={{ height: 70, width: "auto", maxHeight: 90 }}
                  />
                </Box>
              );
            } else if (selectedTheme === "phrma") {
              return (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1.5, px: 3 }}>
                  <Typography sx={{ fontWeight: 700, color: T.text, mr: 2, lineHeight: 1, fontFamily: "'DM Sans', sans-serif", fontSize: "15px" }}>
                    340B Research
                  </Typography>
                  <img
                    src={`${process.env.PUBLIC_URL}/static/images/phrma.png`}
                    alt="Phrma Logo"
                    style={{ height: 32, width: "auto", maxHeight: 40 }}
                  />
                </Box>
              );
            } else if (selectedTheme === "selfdrive") {
              return (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1.5, px: 3 }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/static/images/avia.webp`}
                    alt="AVIA Logo"
                    style={{ height: 34, width: "auto", maxHeight: 44 }}
                  />
                </Box>
              );
            } else if (selectedTheme === "intuit") {
              return (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1.5, px: 3 }}>
              
                  <img
                    src={`${process.env.PUBLIC_URL}/static/images/Intuit.png`}
                    alt="Intuit Logo"
                    style={{ height: 32, width: "auto", maxHeight: 40 }}
                  />
                </Box>
              );
            } else{
              return (
                <Box sx={{ py: 1.75, px: 2.5, justifyContent: "center", display: "flex", alignItems: "center",
                }}>
                  <Typography sx={{
                    fontWeight: 700,
                    color: T.text,
                    lineHeight: 1,
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "15px",
                    letterSpacing: "-0.01em",
                  }}>
                    {sectionTitle}
                  </Typography>
                </Box>
              );
            }
          })()}

          {/* ── Top-level tabs: Data / Ask AI — segmented control ── */}
          {showChatTab && (
            <Box sx={{ px: 2.5, pb: 1, pt: 0.25 }}>
              <Box sx={{
                display: "flex",
                p: "3px",
                borderRadius: "10px",
                bgcolor: "rgba(15,23,42,0.05)",
                border: `1px solid ${T.border}`,
              }}>
                {[
                  { key: "data", label: "Data", Icon: BarChartIcon },
                  { key: "chat", label: "Ask AI", Icon: SmartToyOutlinedIcon },
                ].map(({ key, label, Icon }) => {
                  const isActive = activeTab === key;
                  return (
                    <ButtonBase
                      key={key}
                      onClick={() => setActiveTab(key)}
                      sx={{
                        flex: 1,
                        py: 0.65,
                        px: 1.5,
                        borderRadius: "7px",
                        bgcolor: isActive ? "#ffffff" : "transparent",
                        boxShadow: isActive ? "0 1px 4px rgba(15,23,42,0.1)" : "none",
                        color: isActive ? T.accent : T.textMuted,
                        fontSize: "12px",
                        fontWeight: isActive ? 700 : 500,
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: "0.01em",
                        transition: "all 0.18s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        "&:hover": { color: isActive ? T.accent : T.text },
                        "&:focus": { outline: "none" },
                      }}
                    >
                      <Icon sx={{ fontSize: 13 }} />
                      {label}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Mode Tabs (Data tab only) — primary / secondary hierarchy */}
          {activeTab === "data" && (
            <Box sx={{ px: 2.5, pb: 1.5, display: "flex", flexDirection: "row", gap: 0.75 }}>
              {/* Explore Variable — primary action */}
              {(() => {
                const isActive = ActionMode === "explore";
                return (
                  <ButtonBase
                    disableRipple
                    onClick={() => handleActionModeChange("explore")}
                    sx={{
                      flex: 1,
                      py: 0.85,
                      px: 1.5,
                      borderRadius: "8px",
                      border: `1px solid ${isActive ? T.accent : T.border}`,
                      bgcolor: isActive ? T.accent : "#ffffff",
                      color: isActive ? "#ffffff" : T.textMuted,
                      fontSize: "12px",
                      fontWeight: 600,
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.01em",
                      transition: "all 0.18s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: isActive ? "0 2px 8px rgba(15,159,171,0.25)" : "none",
                      "&:hover": {
                        bgcolor: isActive ? "#0d8f9a" : T.activeTab,
                        borderColor: T.accent,
                        color: isActive ? "#ffffff" : T.accent,
                      },
                      "&:focus": { outline: "none" },
                    }}
                  >
                    Explore Variable
                  </ButtonBase>
                );
              })()}

              {/* Find Target Areas — secondary action */}
              {(() => {
                const isActive = ActionMode === "filter";
                const disabled = false;
                return (
                  <ButtonBase
                    disableRipple
                    disabled={disabled}
                    onClick={() => { if (!disabled) handleActionModeChange("filter"); }}
                    sx={{
                      flex: 1,
                      py: 0.85,
                      px: 1.5,
                      borderRadius: "8px",
                      border: `1px solid ${disabled ? T.border : isActive ? T.chipActiveBorder : T.border}`,
                      bgcolor: disabled ? "#f9fafb" : isActive ? T.activeTab : "transparent",
                      color: disabled ? "#c0c8d4" : isActive ? T.accent : T.textMuted,
                      fontSize: "12px",
                      fontWeight: isActive ? 600 : 400,
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.01em",
                      transition: "all 0.18s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: disabled ? "not-allowed" : "pointer",
                      pointerEvents: disabled ? "none" : "auto",
                      opacity: disabled ? 0.6 : 1,
                      "&:hover": disabled ? {} : {
                        borderColor: T.chipActiveBorder,
                        bgcolor: T.activeTab,
                        color: T.accent,
                      },
                      "&:focus": { outline: "none" },
                    }}
                  >
                    Find Target Areas
                  </ButtonBase>
                );
              })()}

            </Box>
          )}
        </Box>

        {/* ── Explore Mode ── */}
        {activeTab === "data" && ActionMode === "explore" && (
          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
          }}>

            {/* Variable Selector */}
            <Box id="tour-variable-selector" sx={{ p: 2.5, borderBottom: `1px solid ${T.border}` }}>
              <SectionLabel>{demo ? "Tracking Questions" : "Data Variable"}</SectionLabel>
              <Box sx={{ mt: 1.25 }}>
                <Select
                  value={selectedVariable ?? ""}
                  onChange={(e) => onVariableChange?.(e.target.value)}
                  displayEmpty
                  renderValue={(v) => groupsById?.[v]?.label || v || ""}
                  size="small"
                  fullWidth
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    marginThreshold: 0,
                    PaperProps: {
                      sx: {
                        bgcolor: T.surface,
                        border: `1px solid ${T.borderStrong}`,
                        borderRadius: "10px",
                        mt: 0.5,
                        boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
                        maxHeight: 360,
                        overflowY: "auto",
                        maxWidth: 260,
                        "& .MuiMenuItem-root": {
                          fontSize: "13px",
                          color: T.textMuted,
                          fontFamily: "'DM Sans', sans-serif",
                          py: 0.75,
                          px: 2,
                          borderRadius: "6px",
                          mx: 0.5,
                          "&:hover": { bgcolor: T.surfaceHover, color: T.text },
                          "&.Mui-selected": { bgcolor: T.accentDim, color: T.accent, fontWeight: 600 },
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: T.chip,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: T.border,
                      transition: "border-color 0.15s",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.borderStrong },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.accent },
                    "& .MuiSelect-select": {
                      color: T.text,
                      fontSize: "13px",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 500,
                      textAlign: "left !important",
                      display: "flex !important",
                      alignItems: "center !important",
                      py: 1.1,
                    },
                    "& .MuiSvgIcon-root": { color: T.textFaint },
                  }}
                >
         {(() => {
                      const rawVars = Object.keys(groupsById);
                      // A config either carries explicit topical subgroups
                      // (polAggregators: Personal Finances, Employment, …) or
                      // none, in which case questions are sectioned by wave
                      // coverage: the tracking set first, then each single-wave
                      // batch, newest wave first.
                      //
                      // Ordering happens here rather than in the aggregators so
                      // groupsById insertion order is untouched — it drives the
                      // categorical colour cycling in buildLabelsAndColors, and
                      // resorting the config would repaint unrelated charts.
                      const hasExplicitSubgroups = rawVars.some(v => groupsById[v]?.subgroup);
                      const waveSectionOf = (v) => {
                        const w = (groupsById[v]?.waves || []).map(Number).filter(Number.isFinite);
                        if (w.length > 1) return { label: "Tracking questions", rank: 0, wave: Infinity };
                        if (w.length === 1) return { label: `Wave ${w[0]} questions`, rank: 1, wave: w[0] };
                        return { label: "Other questions", rank: 2, wave: -Infinity };
                      };
                      // Only worth sectioning by wave when the config actually
                      // carries wave metadata and it splits into more than one
                      // group — otherwise it's a single pointless header.
                      const waveSectionCount = hasExplicitSubgroups
                        ? 0
                        : new Set(rawVars.filter(v => !groupsById[v]?.hidden).map(v => waveSectionOf(v).label)).size;
                      const visibleVarCount = rawVars.filter(v => !groupsById[v]?.hidden).length;
                      const useWaveSections =
                        waveSectionCount > 1 && visibleVarCount >= MIN_VARS_FOR_WAVE_SECTIONS;
                      const vars = useWaveSections
                        ? rawVars
                            .map((v, i) => ({ v, i, sec: waveSectionOf(v) }))
                            .sort((a, b) =>
                              a.sec.rank - b.sec.rank ||
                              b.sec.wave - a.sec.wave ||
                              a.i - b.i)
                            .map(({ v }) => v)
                        : rawVars;
                      const sectionOf = (v) =>
                        groupsById[v]?.subgroup || (useWaveSections ? waveSectionOf(v).label : null);
                      const hasSubgroups = hasExplicitSubgroups || useWaveSections;
                      const items = [];
                      let lastSubgroup = null;
                      for (const variable of vars) {
                        const option = groupsById[variable];
                        if (option?.hidden) continue;
                        const disabled = demo && option?.hidden_in_demo;
                        const section = sectionOf(variable);
                        if (hasSubgroups && section && section !== lastSubgroup) {
                          lastSubgroup = section;
                          items.push(
                            <ListSubheader
                              key={`subgroup-${section}`}
                              sx={{
                                fontSize: "10px",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: T.accent,
                                bgcolor: T.surface,
                                fontFamily: "'DM Sans', sans-serif",
                                lineHeight: "28px",
                                px: 2,
                                pt: lastSubgroup === section ? 0 : 0.5,
                              }}
                            >
                              {section}
                            </ListSubheader>
                          );
                        }
                        items.push(
                          <MenuItem
                            key={variable}
                            value={variable}
                            disabled={disabled}
                            sx={{ opacity: disabled ? 0.65 : 1 }}
                          >
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                              <Box component="span">{option?.label || variable}</Box>
                            </Box>
                          </MenuItem>
                        );
                      }
                      if (demo) {
                        const totalDemo = Object.keys(groupsById).filter(v => !groupsById[v]?.hidden && !groupsById[v]?.hidden_in_demo).length;
                        const isTracking = selectedTheme === "demo-pol";
                        const footerText = isTracking
                          ? `Showing ${totalDemo} example tracking questions. Full access includes 15 questions across politics, personal finances, economic outlook, employment, and business.`
                          : `Showing ${totalDemo} example issue questions. We add new topical questions each wave. Full access includes the complete question set.`;
                        items.push(
                          <Box key="__demo-footer" sx={{ px: 2, py: 1.25, borderTop: "1px solid rgba(0,0,0,0.07)", mt: 0.5 }}>
                            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4 }}>
                              {footerText}
                            </Typography>
                          </Box>
                        );
                      }
                      return items;
                    })()}
                </Select>
              </Box>

              {selected?.fulltext && (
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: T.textFaint, mt: "2px", flexShrink: 0 }} />
                    
                    <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                      <Typography sx={{ fontSize: "12px", color: T.textFaint, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
                        {selected.fulltext}
                      </Typography>
                    </Collapse>
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 1.5 }}>
                <QuestionFilterComponent
                  groupsById={groupsById}
                  selectedVariable={selectedVariable}
                  onVariableChange={onVariableChange}
                  preferenceFilter={preferenceFilter}
                  onPreferenceFilterChange={onPreferenceFilterChange}
                  ActionMode={ActionMode}
                  selectedFeatureData={selectedFeatureData}
                />
              </Box>
            </Box>

            {/* Demographics Filter */}
            <StyledAccordion id="tour-demographics">
              <StyledAccordionSummary>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TuneIcon sx={{ fontSize: 14, color: T.textFaint }} />
                  <SectionLabel>Filter by Demographics</SectionLabel>
                </Stack>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: T.textFaint, mb: 1.75, fontFamily: "'DM Sans', sans-serif" }}>
                  Explore how different groups respond.
                </Typography>

                <Stack spacing={1.75}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <DemographicGroup icon={PersonIcon} label="Gender" iconColor="#60a5fa">
                        {[[1, "Men"], [2, "Women"]].map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={genderFilter === val}
                            onClick={() => onGenderChange?.(toggleVal(genderFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      <DemographicGroup icon={CakeIcon} label="Age" iconColor="#a78bfa">
                        {[[1, "18–34"], [2, "35–64"], [3, "65+"]].map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={ageFilter === val}
                            onClick={() => onAgeChange?.(toggleVal(ageFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                  </Stack>

                  <DemographicGroup icon={SchoolIcon} label="Education" iconColor="#fbbf24">
                    {[[1, "No degree"], [2, "Degree"]].map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={educationFilter === val}
                        onClick={() => onEducationChange?.(toggleVal(educationFilter, val))} />
                    ))}
                  </DemographicGroup>

                 <DemographicGroup icon={GroupsIcon} label="Ethnicity" iconColor="#34d399">
                    {[[1, "White"], [2, "Black"], [3, "Hispanic"], [4, "Asian"], [5, "Other"]].map(([val, lbl]) => (
                      <FilterChip
                        key={val}
                        label={lbl}
                        active={ethnicityFilter === val}
                        disabled={demo}
                        onClick={() => onEthnicityChange?.(toggleVal(ethnicityFilter, val))}
                      />
                    ))}
                    {demo && (
                      <Typography
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          fontSize: "10px",
                          color: T.textFaint,
                          fontStyle: "italic",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Available in full version
                      </Typography>
                    )}
                  </DemographicGroup>

                  <DemographicGroup icon={HowToVoteIcon} label="Party Registration" iconColor="#f87171">
                    {[[1, "Democrat"], [2, "Republican"], [3, "Independent"], [4, "None"]].map(([val, lbl]) => (
                      <FilterChip
                        key={val}
                        label={lbl}
                        active={partyFilter === val}
                        disabled={demo}
                        onClick={() => onPartyChange?.(toggleVal(partyFilter, val))}
                      />
                    ))}
                    {demo && (
                      <Typography
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          fontSize: "10px",
                          color: T.textFaint,
                          fontStyle: "italic",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Available in full version
                      </Typography>
                    )}
                  </DemographicGroup>

                <DemographicGroup icon={HowToVoteIcon} label="2024 Presidential Vote" iconColor="#800080">
                    {[[1, "Trump"], [2, "Harris"], [3, "Other"], [4, "Did Not Vote"]].map(([val, lbl]) => (
                      <FilterChip
                        key={val}
                        label={lbl}
                        active={vote24Filter === val}
                        disabled={demo}
                        onClick={() => onVote24Change?.(toggleVal(vote24Filter, val))}
                      />
                    ))}
                    {demo && (
                      <Typography
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          fontSize: "10px",
                          color: T.textFaint,
                          fontStyle: "italic",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Available in full version
                      </Typography>
                    )}
                  </DemographicGroup>
                </Stack>

                {onClearAll && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <ButtonBase
                      onClick={onClearAll}
                      sx={{
                        fontSize: "11px",
                        color: T.textFaint,
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        letterSpacing: "0.03em",
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "6px",
                        border: `1px solid ${T.border}`,
                        transition: "all 0.15s",
                        "&:hover": { color: T.text, borderColor: T.borderStrong, bgcolor: T.surfaceHover },
                      }}
                    >
                      Clear all
                    </ButtonBase>
                  </Box>
                )}
              </AccordionDetails>
            </StyledAccordion>

            {/* Compare Geographies Chart */}
            {(() => {
              const group = groupsById?.[selectedVariable];
              const fmtVal = (v) =>
                v == null || !Number.isFinite(v)
                  ? "—"
                  : group?.type === "price_elast"
                    ? `$${(v / 100).toFixed(2)}`
                    : `${v.toFixed(1)}%`;

              const labels2 = colorLabels || {};
              const colors2 = colorMap || {};
              const byLabel2 = new Map();
              const upsert2 = (arr, field) => {
                (arr || []).forEach(({ key, label, value }) => {
                  const k = key || Object.keys(labels2).find((lk) => labels2[lk] === label);
                  const displayLabel = labels2[k] || label || "(no label)";
                  const row = byLabel2.get(displayLabel) || { label: displayLabel, key: k, national: null, state: null, selected: null, unfilteredNational: null, unfilteredState: null, unfilteredSelected: null, color: T.accent };
                  const num = Number(value);
                  row[field] = Number.isFinite(num) ? num : null;
                  if (k && colors2[k]) row.color = colors2[k];
                  if (k) row.key = k;
                  byLabel2.set(displayLabel, row);
                });
              };
              upsert2(selectedFeatureData?.national, "national");
              upsert2(selectedFeatureData?.state, "state");
              upsert2(selectedFeatureData?.selected, "selected");
              upsert2(selectedFeatureData?.unfilteredNational, "unfilteredNational");
              upsert2(selectedFeatureData?.unfilteredState, "unfilteredState");
              upsert2(selectedFeatureData?.unfilteredSelected, "unfilteredSelected");

              const order2 = (groupsById?.[selectedVariable]?.keys || []).map((k) => labels2[k] || k);
              const combined2 = Array.from(byLabel2.values()).sort((a, b) => {
                const ia = order2.indexOf(a.label), ib = order2.indexOf(b.label);
                return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
              });

              const sAlmostEqual = (rows, a, b, tol = 0.01) => {
                let compared = false;
                for (const r of rows) {
                  const va = r[a], vb = r[b];
                  if (va == null && vb == null) continue;
                  if (va == null || vb == null) return false;
                  compared = true;
                  if (Math.abs(va - vb) > tol) return false;
                }
                return compared;
              };
              const sAllNull = (rows, key) => rows.every((r) => r[key] == null || !Number.isFinite(r[key]) || Math.abs(r[key]) < 1e-9);

              const rawWaveSeries = Array.isArray(selectedFeatureData?.waveSeries) ? selectedFeatureData.waveSeries : [];
              const mkKeys = Array.isArray(selectedFeatureData?.waveSeriesKeys) ? selectedFeatureData.waveSeriesKeys : null;
              const mkKeyLabels = selectedFeatureData?.waveSeriesKeyLabels || {};
              const mkKeyColors = selectedFeatureData?.waveSeriesKeyColors || {};
              const getKeyLabel2 = (k) => mkKeyLabels[k] || labels2[k] || k;
              const getKeyColor2 = (k) => mkKeyColors[k] || colors2[k] || T.accent;
              const waveSeries = rawWaveSeries.filter((row) => {
                const geoVals = [row?.national, row?.state, row?.selected]
                  .map((v) => Number(v)).filter(Number.isFinite);
                if (mkKeys?.length) {
                  const mkVals = mkKeys.flatMap((k) => [Number(row?.[k]), Number(row?.[k + "__nat"])]).filter(Number.isFinite);
                  const allVals = [...geoVals, ...mkVals];
                  return allVals.length > 0 && allVals.some((v) => Math.abs(v) > 1e-9);
                }
                if (!geoVals.length) return false;
                return geoVals.some((v) => Math.abs(v) > 1e-9);
              });
              const shouldClearState = !selectedAreaId && !selectedFeatureLevel && !selectedStateName;
              if (shouldClearState && waveSeries.length) {
                waveSeries.forEach((d) => { d.state = null; d.selected = null; });
              }
              const allWaveKeys = mkKeys || [];
              const visibleWaveKeys = activeWaveKeys
                ? allWaveKeys.filter((k) => activeWaveKeys.has(k))
                : allWaveKeys;
              const effectiveWaveKeys = visibleWaveKeys.length > 0 ? visibleWaveKeys : allWaveKeys;
              const selIsState = typeof selectedFeatureLevel === "string" && selectedFeatureLevel === "states";
              const trendWaveSeries = allWaveKeys.length
                ? waveSeries.filter((row) => {
                    const vals = effectiveWaveKeys
                      .flatMap((k) => [Number(row?.[k]), Number(row?.[k + "__nat"])])
                      .filter(Number.isFinite);
                    return vals.length > 0 && vals.some((v) => Math.abs(v) > 1e-9);
                  })
                : waveSeries;
              const hasStateContext = Boolean(selectedStateName);
              const showTrendToggle = compareWaves && (
                allWaveKeys.length
                  ? trendWaveSeries.length >= 2
                  : waveSeries.length >= 2
              );
              const trendData = trendWaveSeries.map((d) => {
                const stateVal = hasStateContext && d.state != null ? Number(d.state) : null;
                const selectedVal = !selIsState
                  ? (d.selected != null ? Number(d.selected) : (stateVal))
                  : null;
                return {
                  ...d, // preserve per-key fields (key, key__nat) for multi-key trend
                  wave: d.wave,
                  national: d.national != null ? Number(d.national) : null,
                  state: Number.isFinite(stateVal) ? stateVal : null,
                  selected: Number.isFinite(selectedVal) ? selectedVal : null,
                };
              });
              const tN = trendData.some((d) => Number.isFinite(d.national));
              const tSt = hasStateContext && trendData.some((d) => Number.isFinite(d.state));
              const tSel = !selIsState && !sAllNull(trendData, "selected") &&
                trendData.some((d) => Number.isFinite(d.selected)) &&
                !sAlmostEqual(trendData, "state", "selected", 0.01);
              const waveNums = trendData.map((r) => Number(r?.wave)).filter(Number.isFinite);
              const waveRangeLabel = waveNums.length >= 2
                ? `Wave ${Math.min(...waveNums)} -> Wave ${Math.max(...waveNums)}`
                : null;
              const hideS = selIsState || sAlmostEqual(combined2, "state", "selected", 0.01) || sAllNull(combined2, "selected");
              const hN = combined2.some((d) => Number.isFinite(d.national));
              const hSt = combined2.some((d) => Number.isFinite(d.state));
              const hSel = !hideS && combined2.some((d) => Number.isFinite(d.selected));
              if (!hN && !hSt && !hSel && !showTrendToggle) return null;
              const natLabel = isDemoCompareMode ? `National (${demoFilterLabel || "filtered"})` : "National";
              const stLabel = isDemoCompareMode ? `${stateLabel} (${demoFilterLabel || "filtered"})` : stateLabel;

              // compute unfiltered key for demo compare mode within this chart context
              const unfLocalKey = (() => {
                if (combined2.some((d) => Number.isFinite(d.unfilteredSelected))) return "unfilteredSelected";
                if (combined2.some((d) => Number.isFinite(d.unfilteredState))) return "unfilteredState";
                if (combined2.some((d) => Number.isFinite(d.unfilteredNational))) return "unfilteredNational";
                return null;
              })();
              const filtLocalKey = hSel ? "selected" : hSt ? "state" : hN ? "national" : null;

              // At sub-state level in demo compare mode, show 3-bar comparison:
              // unfilteredState (everyone in state) + state (filtered in state) + selected (filtered in district)
              const isSubStateDemoMode = isDemoCompareMode && hSel && hSt &&
                combined2.some((d) => Number.isFinite(d.unfilteredState));

              const allVals2 = combined2.flatMap((d) => [
                hN ? d.national : null, hSt ? d.state : null, hSel ? d.selected : null,
                isDemoCompareMode && Number.isFinite(d.unfilteredNational) ? d.unfilteredNational : null,
                isDemoCompareMode && Number.isFinite(d.unfilteredState) ? d.unfilteredState : null,
                isDemoCompareMode && Number.isFinite(d.unfilteredSelected) ? d.unfilteredSelected : null,
              ]).filter(Number.isFinite);
              const rawMin = allVals2.length ? Math.min(...allVals2) : 0;
              const rawMax = allVals2.length ? Math.max(...allVals2) : 0;
              const lower2 = group?.type === "price_elast" ? Math.floor(Math.min(0, rawMin)) : 0;
              const upper2 = group?.type === "price_elast" ? Math.ceil(rawMax) : Math.min(100, Math.ceil(Math.max(10, rawMax)));
              const getSortVal2 = (row) => {
                if (hSel && Number.isFinite(row.selected)) return row.selected;
                if (hSt && Number.isFinite(row.state)) return row.state;
                if (hN && Number.isFinite(row.national)) return row.national;
                return -Infinity;
              };
              const sorted2 = group?.type === "likert"
                ? [...combined2]  // preserve key order for likert questions
                : [...combined2].sort((a, b) => getSortVal2(b) - getSortVal2(a));

              const isLikert2 = group?.type === "likert";
              const toArrL2 = (v) => Array.isArray(v) ? v : (v ? [v] : []);
              const polarityPos2 = isLikert2 ? toArrL2(group?.polarity?.pos) : [];
              const polarityNeg2 = isLikert2 ? toArrL2(group?.polarity?.neg) : [];
              const cellColor2 = (row) => {
                if (!isLikert2) return row.color;
                if (polarityPos2.includes(row.key)) return "#16a34a";
                if (polarityNeg2.includes(row.key)) return "#dc2626";
                return row.color;
              };

              const perRow2 = sorted2.length > 15 ? 30 : 26;
              const cHeight = Math.max(240, perRow2 * Math.max(sorted2.length, 1) + 40);
              const longest2 = sorted2.reduce((m, d) => Math.max(m, measureTextWidth(d.label)), 0);
              const yW = Math.min(180, Math.max(50, Math.ceil(longest2) + 10));

              const isTrendMode = showTrendToggle && effectiveChartMode === "trend";

              // ── Movement rows for trend callout ──────────────────────────────
              const movementRows = (() => {
                if (allWaveKeys.length > 0) {
                  return effectiveWaveKeys.map(k => {
                    const pts = trendData
                      .filter(r => Number.isFinite(Number(r?.[k])))
                      .map(r => ({ wave: Number(r.wave), value: Number(r[k]) }));
                    if (pts.length < 2) return null;
                    const first = pts[0], last = pts[pts.length - 1];
                    return {
                      key: k,
                      label: (selectedFeatureData?.waveSeriesKeyLabels?.[k]) || k,
                      delta: last.value - first.value,
                      absDelta: Math.abs(last.value - first.value),
                    };
                  }).filter(Boolean).sort((a, b) => b.absDelta - a.absDelta);
                }
                // Single-key: use most specific geography series
                const seriesKey = hSel ? "selected" : hSt ? "state" : "national";
                const pts = trendData
                  .filter(r => Number.isFinite(Number(r?.[seriesKey])))
                  .map(r => ({ wave: Number(r.wave), value: Number(r[seriesKey]) }));
                if (pts.length < 2) return [];
                const first = pts[0], last = pts[pts.length - 1];
                return [{
                  key: seriesKey,
                  label: selectedFeatureData?.waveSeriesLabel || seriesKey,
                  delta: last.value - first.value,
                  absDelta: Math.abs(last.value - first.value),
                }];
              })();
              const topUp   = movementRows.filter(r => r.delta > 0).sort((a, b) => b.delta - a.delta)[0] || null;
              const topDown = movementRows.filter(r => r.delta < 0).sort((a, b) => a.delta - b.delta)[0] || null;

              // ── Plain-language summary ────────────────────────────────────────
              const plainSummary = (() => {
                if (!sorted2.length) return null;
                const fmt = (v) => Number.isFinite(v) ? `${v.toFixed(1)}%` : null;
                const fmtPp = (v) => v >= 0 ? `+${v.toFixed(1)}pp` : `${v.toFixed(1)}pp`;
                const isSubStateLevel = selectedFeatureLevel && selectedFeatureLevel !== "states";
                const areaName = isSubStateLevel ? selectedAreaLabel : null;
                // Only use the state name if actually selected — don't fall back to "State" literal
                const inArea = areaName || selectedStateName || null;
                const demoContext = (() => {
                  if (!selectedDemographics) return null;
                  const parts = Object.values(selectedDemographics).filter(Boolean).flatMap(v => Array.isArray(v) ? v : [v]);
                  return parts.length ? parts.join(", ") : null;
                })();
                // Short question label for context (e.g. "Economy approval", "Vote intention")
                const questionLabel = (() => {
                  const lbl = group?.label || group?.TargetVariable1?.label || null;
                  if (!lbl || lbl.length > 55) return null;
                  return lbl;
                })();
                // Always use unfiltered values for the blurb — it describes the area overall,
                // not the filtered group. The demoGapSentence handles the filtered comparison.
                const localVal = (row) => {
                  if (isDemoCompareMode) {
                    // Prefer unfiltered series in specificity order
                    if (Number.isFinite(row.unfilteredSelected)) return row.unfilteredSelected;
                    if (Number.isFinite(row.unfilteredState)) return row.unfilteredState;
                    if (Number.isFinite(row.unfilteredNational)) return row.unfilteredNational;
                  }
                  if (hSel && Number.isFinite(row.selected)) return row.selected;
                  if (hSt && Number.isFinite(row.state)) return row.state;
                  if (hN && Number.isFinite(row.national)) return row.national;
                  return null;
                };
                if (isLikert2) {
                  const hasPosData = polarityPos2.some(k => { const r = sorted2.find(x => x.key === k); return r && Number.isFinite(localVal(r)); });
                  if (!hasPosData) return null;
                  const sumPol = (keys) => keys.reduce((s, k) => { const r = sorted2.find(x => x.key === k); const v = r ? localVal(r) : null; return s + (Number.isFinite(v) ? v : 0); }, 0);
                  const netFor = (field) => {
                    // In demo compare mode, use unfiltered counterpart for national/state comparisons
                    const resolvedField = isDemoCompareMode
                      ? (field === "national" ? "unfilteredNational" : field === "state" ? "unfilteredState" : field)
                      : field;
                    const p = polarityPos2.reduce((s, k) => { const r = sorted2.find(x => x.key === k); return s + (Number.isFinite(r?.[resolvedField]) ? r[resolvedField] : 0); }, 0);
                    const n = polarityNeg2.reduce((s, k) => { const r = sorted2.find(x => x.key === k); return s + (Number.isFinite(r?.[resolvedField]) ? r[resolvedField] : 0); }, 0);
                    return p - n;
                  };
                  const pos = sumPol(polarityPos2), neg = sumPol(polarityNeg2), net = pos - neg;
                  let vsState = null, vsNational = null;
                  if (isSubStateLevel && hSt) { const d = net - netFor("state"); if (Math.abs(d) >= 2) vsState = d; }
                  if (inArea && hN) { const d = net - netFor("national"); if (Math.abs(d) >= 2) vsNational = d; }
                  const whoStr = (!isDemoCompareMode && demoContext) ? `Among ${demoContext}` : inArea ? `In ${inArea}` : "Nationally";
                  const qPrefix = questionLabel ? ` on ${questionLabel.toLowerCase()}` : "";
                  let text = net > 5
                    ? `${whoStr}${qPrefix}, ${fmt(pos)} hold a positive view — net ${fmtPp(net)}.`
                    : net < -5
                    ? `${whoStr}${qPrefix}, sentiment is negative: ${fmt(neg)} hold a negative view (net ${fmtPp(net)}).`
                    : `${whoStr}${qPrefix}, views are evenly split — ${fmt(pos)} positive, ${fmt(neg)} negative.`;
                  if (vsState !== null) {
                    text += ` That's ${fmtPp(vsState)} ${vsState > 0 ? "above" : "below"} the ${stateLabel || "state"} average`;
                    text += vsNational !== null ? ` and ${fmtPp(vsNational)} ${vsNational > 0 ? "above" : "below"} the national average.` : ".";
                  } else if (vsNational !== null) {
                    text += ` That's ${fmtPp(vsNational)} ${vsNational > 0 ? "above" : "below"} the national average.`;
                  }
                  return text;
                }
                // Categorical
                const withVals = sorted2.map(r => ({ ...r, val: localVal(r) })).filter(r => Number.isFinite(r.val));
                if (!withVals.length) return null;
                const sv = [...withVals].sort((a, b) => b.val - a.val);
                const top = sv[0], second = sv[1];
                const whoStr = (!isDemoCompareMode && demoContext) ? `Among ${demoContext}` : inArea ? `In ${inArea}` : "Nationally";
                let vsState = null, diff = null;
                const stateRef = isDemoCompareMode ? top.unfilteredState : top.state;
                const natRef = isDemoCompareMode ? top.unfilteredNational : top.national;
                if (isSubStateLevel && hSt && Number.isFinite(stateRef)) { const d = (top.val ?? 0) - stateRef; if (Math.abs(d) >= 2) vsState = d; }
                if (inArea && hN && Number.isFinite(natRef)) { const d = (top.val ?? 0) - natRef; if (Math.abs(d) >= 2) diff = d; }
                const qSuffix = questionLabel ? ` (${questionLabel})` : "";
                let text = second && fmt(second.val)
                  ? `${whoStr}, ${top.label} leads at ${fmt(top.val)}, ahead of ${second.label} at ${fmt(second.val)}${qSuffix}.`
                  : `${whoStr}, ${top.label} is the top response at ${fmt(top.val)}${qSuffix}.`;
                if (vsState !== null) {
                  text += ` That's ${fmtPp(vsState)} ${vsState > 0 ? "above" : "below"} the ${stateLabel || "state"} average`;
                  text += diff !== null ? ` and ${fmtPp(diff)} ${diff > 0 ? "above" : "below"} the national average.` : ".";
                } else if (diff !== null) {
                  text += ` ${top.label} is ${fmtPp(diff)} ${diff > 0 ? "above" : "below"} the national average here.`;
                }
                return text;
              })();

              // ── Demographic gap sentence ──────────────────────────────────────
              const demoGapSentence = (() => {
                if (!isDemoCompareMode) return null;
                // Use sub-state-aware keys: compare filtered district vs unfiltered state, or filtered vs unfiltered at state level
                const gapUnfKey = isSubStateDemoMode ? "unfilteredState" : unfLocalKey;
                const gapFiltKey = isSubStateDemoMode ? "selected" : filtLocalKey;
                if (!gapUnfKey || !gapFiltKey) return null;

                const fmtPp2 = (v) => v >= 0 ? `+${v.toFixed(1)}pp` : `${v.toFixed(1)}pp`;
                const groupRef = demoFilterLabel ? `This group (${demoFilterLabel})` : "This group";
                const isNationalScope = !isSubStateDemoMode && unfLocalKey === "unfilteredNational";
                const areaRef = isSubStateDemoMode
                  ? `${selectedLabel}`
                  : isNationalScope ? "nationally" : (stateLabel ? `${stateLabel}` : "the area");
                const vsRef = isNationalScope ? "the national average" : `${stateLabel || "the area"} overall`;

                if (isLikert2) {
                  const posKeys = polarityPos2, negKeys = polarityNeg2;
                  const sumFrom = (key) => [...posKeys, ...negKeys].reduce((acc, k) => {
                    const row = sorted2.find(r => r.key === k);
                    return { pos: acc.pos + (Number.isFinite(row?.[key]) && posKeys.includes(k) ? row[key] : 0),
                             neg: acc.neg + (Number.isFinite(row?.[key]) && negKeys.includes(k) ? row[key] : 0) };
                  }, { pos: 0, neg: 0 });
                  const filt = sumFrom(gapFiltKey), unf = sumFrom(gapUnfKey);
                  const filtNet = filt.pos - filt.neg;
                  const unfNet = unf.pos - unf.neg;
                  const gap = filtNet - unfNet;
                  if (!Number.isFinite(gap) || Math.abs(gap) < 1) return null;
                  const direction = gap > 0 ? "more positive" : "more negative";
                  const everyoneNet = fmtPp2(unfNet);
                  const groupNet = fmtPp2(filtNet);
                  return isNationalScope
                    ? `${groupRef} lean ${direction} than ${vsRef} (net ${groupNet} vs. ${everyoneNet}).`
                    : `${groupRef} in ${areaRef} lean ${direction} than ${vsRef} (net ${groupNet} vs. ${everyoneNet}).`;
                }

                const withFilt = sorted2.map(r => ({ label: r.label, val: r[gapFiltKey] })).filter(r => Number.isFinite(r.val));
                const withUnf  = sorted2.map(r => ({ label: r.label, val: r[gapUnfKey]  })).filter(r => Number.isFinite(r.val));
                if (!withFilt.length || !withUnf.length) return null;
                const topFilt = [...withFilt].sort((a, b) => b.val - a.val)[0];
                const topUnf  = [...withUnf].sort((a, b) => b.val - a.val)[0];
                const matchUnf = withUnf.find(r => r.label === topFilt.label);
                const gap = topFilt.val - (matchUnf ? matchUnf.val : 0);
                if (Math.abs(gap) < 0.5) return null;
                const differsFrom = topFilt.label !== topUnf.label
                  ? ` Compared to ${vsRef}, which leans ${topUnf.label}.`
                  : "";
                return isNationalScope
                  ? `${groupRef} lean ${topFilt.label} at ${topFilt.val.toFixed(1)}% nationally — ${fmtPp2(Math.abs(gap))} ${gap > 0 ? "higher" : "lower"} than ${vsRef}.${differsFrom}`
                  : `${groupRef} in ${areaRef} lean ${topFilt.label} at ${topFilt.val.toFixed(1)}% — ${fmtPp2(Math.abs(gap))} ${gap > 0 ? "higher" : "lower"} than ${vsRef}.${differsFrom}`;
              })();

              // ── Trend callout ─────────────────────────────────────────────────
              const trendCallout = (() => {
                if (!compareWaves || movementRows.length === 0) return null;
                const waveNums = trendData.map(r => Number(r?.wave)).filter(Number.isFinite);
                if (waveNums.length < 2) return null;
                const firstWave = Math.min(...waveNums);
                const lastWave = Math.max(...waveNums);
                const fmtWave = (w) => {
                  const s = String(w);
                  if (s.length !== 4) return `Wave ${w}`;
                  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                  const yr = `20${s.slice(0, 2)}`;
                  const mo = parseInt(s.slice(2), 10);
                  return `${months[mo - 1] || s.slice(2)} ${yr}`;
                };
                const parts = [];
                if (topUp && Math.abs(topUp.delta) >= 1) parts.push(`"${topUp.label}" is up ${topUp.delta.toFixed(1)}pp since ${fmtWave(firstWave)}.`);
                if (topDown && Math.abs(topDown.delta) >= 1) parts.push(`"${topDown.label}" is down ${Math.abs(topDown.delta).toFixed(1)}pp over the same period.`);
                if (!parts.length) return null;
                const trendPrefix = demoFilterLabel
                  ? `Among ${demoFilterLabel.split(",").length === 1 ? demoFilterLabel : "this group"}, trend`
                  : "Trend";
                return `${trendPrefix} (${fmtWave(firstWave)} → ${fmtWave(lastWave)}): ${parts.join(" ")}`;
              })();

              return (
                <StyledAccordion id="tour-compare-chart" defaultExpanded>
                  <StyledAccordionSummary>
                    <SectionLabel>Compare by Geographies</SectionLabel>
                  </StyledAccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0.5 }}>
                    {/* Plain-language summary + demographic gap + trend callout */}
                    {(() => {
                      const showPlain = plainSummary && !isTrendMode;
                      const showDemo = demoGapSentence && !isTrendMode;
                      if (!showPlain && !trendCallout && !showDemo) return null;
                      return (
                        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: "8px", bgcolor: T.accentDim, border: `1px solid ${T.chipActiveBorder}` }}>
                          {showPlain && (
                            <Typography sx={{ fontSize: "12px", color: T.text, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55 }}>
                              {plainSummary}
                            </Typography>
                          )}
                          {showDemo && (
                            <Typography sx={{ fontSize: "12px", color: "#7c3aed", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, mt: showPlain ? 0.75 : 0, fontWeight: 600 }}>
                              {demoGapSentence}
                            </Typography>
                          )}
                          {trendCallout && (
                            <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, mt: (showPlain || showDemo) ? 0.75 : 0, fontStyle: "italic" }}>
                              {trendCallout}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Filter banner */}
                    {hasDemoFilter && (() => {
                      const pills = Object.entries(selectedDemographics || {}).flatMap(([key, val]) => {
                        const lbls = Array.isArray(val) ? val : val ? [val] : [];
                        return lbls.map(l => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}`);
                      });
                      if (!pills.length) return null;
                      return (
                        <Box sx={{ mb: 1.25, borderRadius: "7px", overflow: "hidden", border: "1.5px solid rgba(3,105,161,0.28)", boxShadow: "0 0 0 3px rgba(3,105,161,0.07)" }}>
                          <Box sx={{ px: 1.25, py: 0.55, bgcolor: "rgba(3,105,161,0.08)", borderBottom: "1px solid rgba(3,105,161,0.22)", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <TuneIcon sx={{ fontSize: 10, color: "#0c4a6e" }} />
                            <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#0c4a6e", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "'DM Sans', sans-serif" }}>
                              Filtered view
                            </Typography>
                          </Box>
                          <Box sx={{ px: 1.25, py: 0.65, bgcolor: "rgba(3,105,161,0.05)", display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {pills.map(pill => (
                              <Box key={pill} sx={{ fontSize: "11px", fontWeight: 600, color: "#0369a1", bgcolor: "rgba(3,105,161,0.08)", border: "1px solid rgba(3,105,161,0.28)", borderRadius: "5px", px: 0.9, py: 0.25, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
                                {pill}
                              </Box>
                            ))}
                          </Box>
                          {isTrendMode && (
                            <Box sx={{ px: 1.25, pb: 0.65, bgcolor: "rgba(3,105,161,0.05)" }}>
                              <Typography sx={{ fontSize: "10px", color: "#0369a1", opacity: 0.85, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}>
                                Trend and national comparison reflect this filtered group only.
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Legend — adapts to chart mode */}
                    {!isTrendMode && (
                      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
                        {(isDemoCompareMode
                          ? [
                              { label: `Everyone${unfLocalKey === "unfilteredNational" ? " nationally" : stateLabel ? ` (${stateLabel})` : ""}`, opacity: 0.35, color: T.accent },
                              { label: `${demoFilterLabel || "Filtered"}${isSubStateDemoMode ? ` — ${selectedLabel}` : ""}`, opacity: 1, color: "#7c3aed" },
                            ]
                          : [
                              hN && { label: natLabel, opacity: 0.35, color: T.accent },
                              hSt && { label: stLabel, opacity: 0.65, color: T.accent },
                              hSel && { label: selectedLabel, opacity: 1, color: T.accent },
                            ].filter(Boolean)
                        ).map(({ label, opacity, color }) => (
                          <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                            <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, opacity }} />
                            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>{label}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    {showTrendToggle && (
                      <Stack direction="row" spacing={1} sx={{ mb: 1.0, alignItems: "center", flexWrap: "wrap" }}>
                        <ToggleButtonGroup
                          value={compareChartMode}
                          exclusive
                          onChange={(e, val) => { if (val) setCompareChartMode(val); }}
                          size="small"
                          sx={{ backgroundColor: "rgba(0,0,0,0.04)", borderRadius: 2, p: 0.25 }}
                        >
                          <ToggleButton value="bars" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.25 }}>Bars</ToggleButton>
                          <ToggleButton value="trend" sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.25 }}>Trend</ToggleButton>
                        </ToggleButtonGroup>
                        {effectiveChartMode === "bars" && waveSelect != null && (
                          <Box sx={{ px: 0.9, py: 0.35, borderRadius: "999px", bgcolor: T.chip, border: `1px solid ${T.border}` }}>
                            <Typography sx={{ fontSize: "10px", color: T.textMuted, fontWeight: 600 }}>
                              Showing Wave {waveSelect}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    )}

                    {(showTrendToggle && effectiveChartMode === "trend") ? (
                      allWaveKeys.length ? (() => {
                        // ── Multi-key per-category trend chart ──────────────────────
                        const effectiveKeys = effectiveWaveKeys;
                        const waveNums2 = trendData.map(r => Number(r?.wave)).filter(Number.isFinite);
                        const hasNatData2 = effectiveKeys.some(k => trendData.some(r => Number.isFinite(Number(r?.[k + "__nat"]))));

                        const movementRows2 = effectiveKeys.map(k => {
                          const pts = trendData
                            .filter(r => Number.isFinite(Number(r?.[k])))
                            .map(r => ({ wave: Number(r.wave), value: Number(r[k]) }));
                          if (pts.length < 2) return null;
                          const natPts = trendData
                            .filter(r => Number.isFinite(Number(r?.[k + "__nat"])))
                            .map(r => ({ wave: Number(r.wave), value: Number(r[k + "__nat"]) }));
                          const statePts = trendData
                            .filter(r => Number.isFinite(Number(r?.[k + "__state"])))
                            .map(r => ({ wave: Number(r.wave), value: Number(r[k + "__state"]) }));
                          const selectedPts = trendData
                            .filter(r => Number.isFinite(Number(r?.[k + "__selected"])))
                            .map(r => ({ wave: Number(r.wave), value: Number(r[k + "__selected"]) }));
                          const first = pts[0], last = pts[pts.length - 1];
                          const delta = last.value - first.value;
                          const lastWaveRow = trendData.find(r => Number(r.wave) === Number(last.wave)) || {};
                          const natEndValue = Number(lastWaveRow?.[k + "__nat"]);
                          const stateEndValue = Number(lastWaveRow?.[k + "__state"]);
                          const selectedEndValue = Number(lastWaveRow?.[k + "__selected"]);
                          const natDelta = natPts.length >= 2 ? natPts[natPts.length - 1].value - natPts[0].value : null;
                          const stateDelta = statePts.length >= 2 ? statePts[statePts.length - 1].value - statePts[0].value : null;
                          const selectedDelta = selectedPts.length >= 2 ? selectedPts[selectedPts.length - 1].value - selectedPts[0].value : delta;
                          return {
                            key: k,
                            label: getKeyLabel2(k),
                            color: getKeyColor2(k),
                            endValue: last.value,
                            natEndValue: Number.isFinite(natEndValue) ? natEndValue : null,
                            stateEndValue: Number.isFinite(stateEndValue) ? stateEndValue : null,
                            selectedEndValue: Number.isFinite(selectedEndValue) ? selectedEndValue : null,
                            natDelta: Number.isFinite(natDelta) ? natDelta : null,
                            stateDelta: Number.isFinite(stateDelta) ? stateDelta : null,
                            selectedDelta: Number.isFinite(selectedDelta) ? selectedDelta : null,
                            delta,
                            absDelta: Math.abs(delta)
                          };
                        }).filter(Boolean).sort((a, b) => b.delta - a.delta);

                        return (
                          <>
                            {/* Category toggle chips */}
                            {allWaveKeys.length > 1 && (
                              <Box sx={{ mb: 1 }}>
                                <ToggleButtonGroup
                                  value={activeWaveKeys ? Array.from(activeWaveKeys) : allWaveKeys}
                                  onChange={(_e, newVal) => {
                                    if (!newVal || newVal.length === 0 || newVal.length === allWaveKeys.length) setActiveWaveKeys(null);
                                    else setActiveWaveKeys(new Set(newVal));
                                  }}
                                  size="small"
                                  sx={{
                                    display: "flex", flexWrap: "wrap", gap: 0.25,
                                    "& .MuiToggleButton-root": {
                                      border: "none", borderRadius: "6px !important", py: 0.3, px: 0.8,
                                      textTransform: "none", fontWeight: 600, fontSize: "0.7rem",
                                      color: "text.secondary", fontFamily: "'DM Sans', sans-serif",
                                      "&.Mui-selected": { backgroundColor: "#fff", color: "text.primary", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
                                      "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                                    },
                                  }}
                                >
                                  {allWaveKeys.map(k => (
                                    <ToggleButton key={k} value={k}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getKeyColor2(k), mr: 0.5, flexShrink: 0 }} />
                                      {getKeyLabel2(k)}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              </Box>
                            )}

                            {/* Header row with vs-National toggle */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                              <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                                Trend across waves (%)
                                {waveNums2.length >= 2 && ` · Wave ${Math.min(...waveNums2)} → Wave ${Math.max(...waveNums2)}`}
                              </Typography>
                              {hasNatData2 && (
                                <ButtonBase
                                  onClick={() => setShowNationalLine(v => !v)}
                                  sx={{ px: 1, py: 0.35, borderRadius: "5px", border: "1px solid", borderColor: showNationalLine ? T.chipActiveBorder : T.border, bgcolor: showNationalLine ? T.chipActive : T.chip, color: showNationalLine ? T.accent : T.textMuted, fontSize: "10px", fontWeight: showNationalLine ? 600 : 400, fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 0.5, transition: "all 0.15s ease", "&:hover": { borderColor: T.borderStrong } }}
                                >
                                  <Box component="span" sx={{ display: "inline-block", width: 12, height: 0, borderTop: "2px dashed currentColor", verticalAlign: "middle", mr: 0.4 }} />
                                  vs National
                                </ButtonBase>
                              )}
                            </Box>

                            <ResponsiveContainer width="100%" height={220}>
                              <LineChart key={`mk-${selectedVariable}-${selectedAreaId}`} data={trendData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                                <XAxis dataKey="wave" tick={{ fontSize: 11, fill: T.textMuted }} tickFormatter={v => `Wave ${v}`} />
                                <YAxis tick={{ fontSize: 10, fill: T.textMuted }} width={40} tickFormatter={v => `${v.toFixed(0)}%`} domain={["auto", "auto"]} />
                                <CartesianGrid stroke={T.border} strokeDasharray="3 3" opacity={0.5} />
                                <RechartsTooltip
                                  content={({ active, label: ttLabel }) => {
                                    if (!active) return null;
                                    const waveRow = trendData.find(r => Number(r.wave) === Number(ttLabel));
                                    const fmt = v => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;
                                    const compareScopeLabel = tSel ? selectedLabel : tSt ? stLabel : natLabel;
                                    return (
                                      <div style={{ background: "#fff", border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontFamily: "'DM Sans', system-ui, sans-serif", minWidth: 160 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Wave {ttLabel}</div>
                                        {effectiveKeys.map(k => {
                                          const selectedVal = waveRow?.[k + "__selected"];
                                          const fallbackVal = waveRow?.[k];
                                          const displayVal = Number.isFinite(Number(selectedVal)) ? selectedVal : fallbackVal;
                                          return (
                                            <div key={k} style={{ marginBottom: 8 }}>
                                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                                                <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getKeyColor2(k) }} />
                                                <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{getKeyLabel2(k)}</span>
                                              </div>
                                              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "2px 8px", paddingLeft: 14 }}>
                                                <span style={{ fontSize: 11, color: "#6b7280" }}>{compareScopeLabel}</span>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: "#1a1d23" }}>{fmt(displayVal)}</span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  }}
                                />
                                {effectiveKeys.map(k => (
                                  <React.Fragment key={k}>
                                    <Line
                                      type="monotone" dataKey={k} stroke={getKeyColor2(k)}
                                      strokeWidth={hoveredTrendKey === k ? 3 : 2}
                                      strokeOpacity={hoveredTrendKey && hoveredTrendKey !== k ? 0.2 : 1}
                                      dot={{ r: 2, fill: getKeyColor2(k), stroke: "#fff", strokeWidth: 1 }}
                                      activeDot={{ r: 4 }} connectNulls
                                      onMouseOver={() => setHoveredTrendKey(k)}
                                      onMouseOut={() => setHoveredTrendKey(null)}
                                    />
                                    {showNationalLine && hasNatData2 && (
                                      <Line key={k + "__nat"} type="monotone" dataKey={k + "__nat"} stroke={getKeyColor2(k)} strokeWidth={1.5} strokeDasharray="4 3" strokeOpacity={hoveredTrendKey && hoveredTrendKey !== k ? 0.12 : 0.5} dot={false} activeDot={false} connectNulls isAnimationActive={false} />
                                    )}
                                  </React.Fragment>
                                ))}
                              </LineChart>
                            </ResponsiveContainer>

                            {/* Movement summary table */}
                            {movementRows2.length > 0 && (
                              <Box sx={{ mt: 1.5, border: `1px solid ${T.border}`, borderRadius: 2, overflow: "hidden" }}>
                                {(() => {
                                  const movementStateHeader = selectedStateName || "State";
                                  const movementNationalHeader = "National";
                                  const movementSelectedHeader = selectedLabel && selectedLabel.length <= 18 ? selectedLabel : "Selected district";
                                  const fmtPP = (v) => Number.isFinite(v) ? `${v > 0 ? "+" : ""}${v.toFixed(1)}pp` : "—";
                                  const deltaTone = (v) => (v > 0.05 ? "#15803d" : v < -0.05 ? "#b91c1c" : T.textFaint);
                                  const deltaArrow = (v) => (v > 0.05 ? "↑" : v < -0.05 ? "↓" : "→");
                                  return (
                                    <>
                                <Box sx={{ px: 1.5, py: 0.9, bgcolor: T.surface, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em" }}>Movement</Typography>
                                  {waveNums2.length >= 2 && (
                                    <Typography sx={{ fontSize: "9.5px", fontWeight: 600, color: "primary.main", bgcolor: "rgba(25,118,210,0.07)", border: "1px solid rgba(25,118,210,0.15)", borderRadius: "4px", px: 0.75, py: 0.25 }}>
                                      Wave {Math.min(...waveNums2)} → Wave {Math.max(...waveNums2)}
                                    </Typography>
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    display: "grid",
                                    gridTemplateColumns: hasNatData2
                                      ? tSt && tSel
                                        ? "minmax(0, 1fr) 76px 92px 108px"
                                        : tSt
                                        ? "minmax(0, 1fr) 76px 92px"
                                        : "minmax(0, 1fr) 76px"
                                      : tSt && tSel
                                      ? "minmax(0, 1fr) 92px 108px"
                                      : tSt
                                      ? "minmax(0, 1fr) 92px"
                                      : "minmax(0, 1fr) 108px",
                                    columnGap: 1,
                                    borderBottom: `1px solid ${T.border}`,
                                    bgcolor: "#fafbfc",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                    Category
                                  </Typography>
                                  {hasNatData2 && (
                                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right", lineHeight: 1.1, whiteSpace: "normal" }}>
                                      {movementNationalHeader}
                                    </Typography>
                                  )}
                                  {tSt && (
                                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right", lineHeight: 1.1, whiteSpace: "normal" }}>
                                      {movementStateHeader}
                                    </Typography>
                                  )}
                                  {tSel && (
                                    <Typography
                                      sx={{
                                        fontSize: "9.5px",
                                        fontWeight: 700,
                                        color: T.textFaint,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                        textAlign: "right",
                                        lineHeight: 1.1,
                                        whiteSpace: "normal",
                                      }}
                                    >
                                      {movementSelectedHeader}
                                    </Typography>
                                  )}
                                </Box>
                                {movementRows2.map((row, i) => {
                                  return (
                                    <Box
                                      key={row.key}
                                      sx={{
                                        px: 1.5,
                                        py: 0.7,
                                        display: "grid",
                                        gridTemplateColumns: hasNatData2
                                          ? tSt && tSel
                                            ? "minmax(0, 1fr) 76px 92px 108px"
                                            : tSt
                                            ? "minmax(0, 1fr) 76px 92px"
                                            : "minmax(0, 1fr) 76px"
                                          : tSt && tSel
                                          ? "minmax(0, 1fr) 92px 108px"
                                          : tSt
                                          ? "minmax(0, 1fr) 92px"
                                          : "minmax(0, 1fr) 108px",
                                        columnGap: 1,
                                        alignItems: "center",
                                        bgcolor: (row.selectedDelta ?? row.delta) > 0.05 ? "rgba(22,163,74,0.04)" : (row.selectedDelta ?? row.delta) < -0.05 ? "rgba(220,38,38,0.04)" : "transparent",
                                        borderBottom: i < movementRows2.length - 1 ? `1px solid ${T.border}` : "none"
                                      }}
                                    >
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: row.color, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: "11.5px", fontWeight: 500, color: T.text, fontFamily: "'DM Sans', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</Typography>
                                      </Box>
                                      {hasNatData2 && (
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: deltaTone(row.natDelta), fontFamily: "'DM Sans', sans-serif", textAlign: "right" }}>
                                          {deltaArrow(row.natDelta)} {fmtPP(row.natDelta)}
                                        </Typography>
                                      )}
                                      {tSt && (
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: deltaTone(row.stateDelta), fontFamily: "'DM Sans', sans-serif", textAlign: "right" }}>
                                          {deltaArrow(row.stateDelta)} {fmtPP(row.stateDelta)}
                                        </Typography>
                                      )}
                                      {tSel && (
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: deltaTone(row.selectedDelta), fontFamily: "'DM Sans', sans-serif", textAlign: "right" }}>
                                          {deltaArrow(row.selectedDelta)} {fmtPP(row.selectedDelta)}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                                    </>
                                  );
                                })()}
                              </Box>
                            )}
                          </>
                        );
                      })() : (
                        // ── Single-key geo-comparison trend chart (original) ─────────
                        <>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                            <Typography sx={{ fontSize: "11px", color: T.textFaint }}>
                              Trend across waves (%, weighted)
                            </Typography>
                            {waveRangeLabel && (
                              <Box sx={{ px: 0.8, py: 0.25, borderRadius: "999px", bgcolor: T.chip, border: `1px solid ${T.border}` }}>
                                <Typography sx={{ fontSize: "10px", color: T.textMuted, fontWeight: 600 }}>
                                  {waveRangeLabel}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart
                              key={`${selectedVariable}-${preferenceFilter}-${selectedAreaId}`}
                              data={trendData}
                              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                            >
                              <XAxis dataKey="wave" tick={{ fontSize: 11, fill: T.textMuted }} tickFormatter={(v) => `Wave ${v}`} />
                              <YAxis tick={{ fontSize: 10, fill: T.textMuted }} width={40} tickFormatter={(v) => `${v.toFixed(0)}%`} domain={["auto", "auto"]} />
                              <CartesianGrid stroke={T.border} strokeDasharray="3 3" opacity={0.5} />
                              <RechartsTooltip
                                content={({ active, payload, label: ttLabel }) => {
                                  if (!active || !payload?.length) return null;
                                  const fmt = (v) => v == null || isNaN(v) ? "—" : group?.type === "price_elast" ? `$${(v / 100).toFixed(2)}` : `${v.toFixed(1)}%`;
                                  const entries = [
                                    (tSel
                                      ? { name: selectedLabel, value: payload.find((p) => p.dataKey === "selected")?.value, opacity: 1 }
                                      : tSt
                                      ? { name: stLabel, value: payload.find((p) => p.dataKey === "state")?.value, opacity: 0.65 }
                                      : { name: natLabel, value: payload.find((p) => p.dataKey === "national")?.value, opacity: 0.35 })
                                  ].filter(Boolean);
                                  return (
                                    <div style={{ background: "#ffffff", border: `1px solid ${T.borderStrong}`, borderRadius: 10, padding: "10px 12px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontSize: 12, lineHeight: 1.5, maxWidth: 220, fontFamily: "'DM Sans', sans-serif" }}>
                                      <div style={{ fontWeight: 700, marginBottom: 6, color: T.text }}>Wave {ttLabel}</div>
                                      {selectedFeatureData?.waveSeriesLabel && (
                                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                                          Metric: {selectedFeatureData.waveSeriesLabel}
                                        </div>
                                      )}
                                      {entries.map((e, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: T.accent, opacity: e.opacity }} />
                                            <span style={{ color: T.textMuted }}>{e.name}</span>
                                          </div>
                                          <span style={{ fontWeight: 700, color: T.text }}>{fmt(e.value)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }}
                              />
                              {tN && <Line type="monotone" dataKey="national" stroke={T.accent} strokeOpacity={0.35} strokeWidth={2} dot={{ r: 2 }} />}
                              {tSt && <Line type="monotone" dataKey="state" stroke={T.accent} strokeOpacity={0.65} strokeWidth={2.4} dot={{ r: 2 }} />}
                              {tSel && <Line type="monotone" dataKey="selected" stroke={T.accent} strokeOpacity={1} strokeWidth={2.8} dot={{ r: 2.5 }} />}
                            </LineChart>
                          </ResponsiveContainer>
                        </>
                      )
                    ) : (
                      <ResponsiveContainer width="100%" height={cHeight}>
                        <BarChart data={sorted2} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 4 }}>
                          <XAxis type="number" domain={[lower2, upper2]} hide />
                          <YAxis type="category" dataKey="label" width={yW}
                            tick={({ x, y, payload }) => {
                              const text = String(payload?.value ?? "");
                              const words = text.split(" ");
                              const lines2 = [];
                              let line2 = [];
                              const lw = (arr) => measureTextWidth(arr.join(" "));
                              words.forEach((w) => {
                                const test = [...line2, w];
                                if (lw(test) > Math.min(yW - 12, 160) && line2.length) {
                                  lines2.push(line2.join(" ")); line2 = [w];
                                } else { line2 = test; }
                              });
                              if (line2.length) lines2.push(line2.join(" "));
                              return (
                                <text x={x - 5} y={y} textAnchor="end" fontSize={11} fill={T.textMuted} style={{ pointerEvents: "none", fontFamily: "'DM Sans', sans-serif" }}>
                                  {lines2.map((t, i) => <tspan key={i} x={x - 5} dy={i === 0 ? 0 : 12}>{t}</tspan>)}
                                </text>
                              );
                            }}
                          />
                          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke={T.border} opacity={0.5} />
                          <RechartsTooltip
                            content={({ active, payload, label: ttLabel }) => {
                              if (!active || !payload?.length) return null;
                              const fmt = (v) => v == null || isNaN(v) ? "—" : group?.type === "price_elast" ? `$${(v / 100).toFixed(2)}` : `${v.toFixed(1)}%`;
                              const demoUnfKey = isSubStateDemoMode ? "unfilteredState" : unfLocalKey;
                              const demoFiltKey = isSubStateDemoMode ? "selected" : filtLocalKey;
                              const everyoneLabel = `Everyone${unfLocalKey === "unfilteredNational" ? " nationally" : stateLabel ? ` (${stateLabel})` : ""}`;
                              const filtLabel = `${demoFilterLabel || "Filtered"}${isSubStateDemoMode ? ` — ${selectedLabel}` : ""}`;
                              const entries = isDemoCompareMode ? [
                                demoUnfKey && { name: everyoneLabel, value: payload.find((p) => p.dataKey === demoUnfKey)?.value, opacity: 0.4, dotColor: payload[0]?.payload?.color || T.accent },
                                demoFiltKey && { name: filtLabel, value: payload.find((p) => p.dataKey === demoFiltKey)?.value, opacity: 1, dotColor: "#7c3aed" },
                              ].filter(Boolean) : [
                                hN && { name: natLabel, value: payload.find((p) => p.dataKey === "national")?.value, opacity: 0.35 },
                                hSt && { name: stLabel, value: payload.find((p) => p.dataKey === "state")?.value, opacity: 0.65 },
                                hSel && { name: selectedLabel, value: payload.find((p) => p.dataKey === "selected")?.value, opacity: 1 },
                              ].filter(Boolean);
                              return (
                                <div style={{ background: "#ffffff", border: `1px solid ${T.borderStrong}`, borderRadius: 10, padding: "10px 12px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontSize: 12, lineHeight: 1.5, maxWidth: 200, fontFamily: "'DM Sans', sans-serif" }}>
                                  <div style={{ fontWeight: 700, marginBottom: 6, color: T.text, whiteSpace: "normal" }}>{ttLabel}</div>
                                  {entries.map((e, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: payload[0]?.payload?.color || T.accent, opacity: e.opacity }} />
                                        <span style={{ color: T.textMuted }}>{e.name}</span>
                                      </div>
                                      <span style={{ fontWeight: 700, color: T.text }}>{fmt(e.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          {isDemoCompareMode ? (
                            <>
                              <Bar dataKey={isSubStateDemoMode ? "unfilteredState" : unfLocalKey} barSize={8} radius={[3, 3, 3, 3]}>
                                {sorted2.map((row, i) => <Cell key={`uf-${i}`} fill={cellColor2(row)} fillOpacity={0.3} cursor="pointer" />)}
                              </Bar>
                              <Bar dataKey={isSubStateDemoMode ? "selected" : filtLocalKey} barSize={8} radius={[3, 3, 3, 3]}>
                                {sorted2.map((row, i) => <Cell key={`fl-${i}`} fill="#7c3aed" fillOpacity={0.85} cursor="pointer" />)}
                              </Bar>
                            </>
                          ) : (
                            <>
                              {hN && <Bar dataKey="national" barSize={8} radius={[3, 3, 3, 3]}>
                                {sorted2.map((row, i) => <Cell key={`n-${i}`} fill={cellColor2(row)} fillOpacity={0.3} cursor="pointer" />)}
                              </Bar>}
                              {hSt && <Bar dataKey="state" barSize={8} radius={[3, 3, 3, 3]}>
                                {sorted2.map((row, i) => <Cell key={`s-${i}`} fill={cellColor2(row)} fillOpacity={0.6} cursor="pointer" />)}
                              </Bar>}
                              {hSel && <Bar dataKey="selected" barSize={8} radius={[3, 3, 3, 3]}>
                                {sorted2.map((row, i) => <Cell key={`sel-${i}`} fill={cellColor2(row)} fillOpacity={1} cursor="pointer" />)}
                              </Bar>}
                            </>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    )}

                    {/* Data Table */}
                    {!compareWaves && (
                      <Box sx={{ mt: 1.5, overflowX: "auto" }}>
                        <Table
                          size="small"
                          sx={{
                            width: "100%",
                            tableLayout: "fixed",
                            "& .MuiTableCell-root": {
                              fontFamily: "'DM Sans', sans-serif",
                              borderColor: T.border,
                              fontSize: "11px",
                              px: 0.75,
                              py: 0.6,
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: "40%", color: T.textFaint, fontWeight: 700, fontSize: "9.5px", letterSpacing: "0.07em", textTransform: "uppercase", bgcolor: "transparent" }}>
                                Label
                              </TableCell>
                              {isDemoCompareMode ? (
                                <>
                                  <TableCell align="right" sx={{ width: "28%", color: T.textMuted, fontWeight: 700, fontSize: "9px", letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.15 }}>
                                    Everyone{stateLabel ? ` (${stateLabel})` : ""}
                                  </TableCell>
                                  <TableCell align="right" sx={{ width: "32%", color: "#7c3aed", fontWeight: 700, fontSize: "9px", letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.15 }}>
                                    {demoFilterLabel || "Filtered"}{isSubStateDemoMode ? ` (district)` : ""}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  {hN && (
                                    <TableCell align="right" sx={{ width: "20%", color: T.textFaint, fontWeight: 700, fontSize: "9px", letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.15 }}>
                                      {natLabel}
                                    </TableCell>
                                  )}
                                  {hSt && (
                                    <TableCell align="right" sx={{ width: "20%", color: T.textFaint, fontWeight: 700, fontSize: "9px", letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.15 }}>
                                      {stLabel}
                                    </TableCell>
                                  )}
                                  {hSel && (
                                    <TableCell align="right" sx={{ width: "20%", color: T.textFaint, fontWeight: 700, fontSize: "9px", letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.15 }}>
                                      {selectedLabel}
                                    </TableCell>
                                  )}
                                </>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sorted2.map((row) => {
                              if (isDemoCompareMode) {
                                const unfVal = isSubStateDemoMode ? row["unfilteredState"] : (unfLocalKey ? row[unfLocalKey] : null);
                                const filtVal = isSubStateDemoMode ? row["selected"] : (filtLocalKey ? row[filtLocalKey] : null);
                                const diff = Number.isFinite(filtVal) && Number.isFinite(unfVal) ? filtVal - unfVal : null;
                                const diffColor = diff == null ? "#7c3aed"
                                  : diff > 1  ? "#16a34a"
                                  : diff < -1 ? "#dc2626"
                                  : "#7c3aed";
                                return (
                                  <TableRow key={row.label} sx={{ "&:hover": { bgcolor: T.accentGlow } }}>
                                    <TableCell sx={{ color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {row.label}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: T.textMuted }}>{fmtVal(unfVal)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: diffColor }}>{fmtVal(filtVal)}</TableCell>
                                  </TableRow>
                                );
                              }
                              const vals = [hN ? row.national : null, hSt ? row.state : null, hSel ? row.selected : null].filter((v) => v != null && Number.isFinite(v));
                              const max = vals.length ? Math.max(...vals) : null;
                              const CellVal = (val) => {
                                const isMax = max != null && val === max;
                                return (
                                  <TableCell align="right" sx={{ fontWeight: isMax ? 700 : 400, color: isMax ? T.accent : T.textMuted }}>
                                    {fmtVal(val)}
                                  </TableCell>
                                );
                              };
                              return (
                                <TableRow key={row.label} sx={{ "&:hover": { bgcolor: T.accentGlow } }}>
                                  <TableCell sx={{ color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {row.label}
                                  </TableCell>
                                  {hN && CellVal(row.national)}
                                  {hSt && CellVal(row.state)}
                                  {hSel && CellVal(row.selected)}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </AccordionDetails>
                </StyledAccordion>
              );
            })()}

            {/* Key Area Statistics */}
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Key Area Statistics</SectionLabel>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <KeyInfo
                  hoverArea={hoverArea}
                  poliInfo={poliInfo}
                  geoLevel={geoLevel}
                  selectedAreaId={selectedFeature?.value ?? null}
                  selectedRowMatchIds={selectedFeature?.rowMatchIds}
                  selectedFeature={selectedFeature}
                  poliInfoToplevel={poliInfoToplevel}
                />
              </AccordionDetails>
            </StyledAccordion>
          </Box>
        )}

        {/* ── Filter Mode ── */}
        {activeTab === "data" && ActionMode === "filter" && (
          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
          }}>
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Find Audiences</SectionLabel>
              </StyledAccordionSummary>
              <AreaSearch
                selectedVariable={selectedVariable}
                selected={selected}
                onVariableChange={onVariableChange}
                groupsById={groupsById}
                detailsOpen={detailsOpen}
                ActionTargetVariables={ActionTargetVariables}
                onActionTargetVariableChange={onActionTargetVariableChange}
                ActionTargetSubVariables={ActionTargetSubVariables}
                handleActionTargetSubVariableChange={handleActionTargetSubVariableChange}
                ActionMode={ActionMode}
                ActionTargetRanges={ActionTargetRanges}
                ActionTargetMAXRanges={ActionTargetMAXRanges}
                handleActionTargetRangesChange={handleActionTargetRangesChange}
                ActionTargetConcentration={ActionTargetConcentration}
                handleActionTargetConcentrationChange={handleActionTargetConcentrationChange}
                selectedDemographics={selectedDemographics}
                audienceInsight={audienceInsight}
                onAudienceInsightRequest={onAudienceInsightRequest}
              />
              <TopAreasPanel
                items={rankedItems}
                onSelect={(area) => onAreaSelect?.(area)}
                initialCount={10}
                loading={!!mapLoading}
                compareMode={compareWaves}
              />
            </StyledAccordion>

            {/* Target Demographics */}
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Target Demographics</SectionLabel>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: T.textFaint, mb: 1.75, fontFamily: "'DM Sans', sans-serif" }}>
                  Choose one or more groups to refine your audience.
                </Typography>

                {/* Workflow Box */}
                <Box sx={{
                  mb: 2,
                  p: 1.75,
                  borderRadius: "10px",
                  border: `1px solid ${T.border}`,
                  bgcolor: T.surface,
                }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 1 }}>
                    Targeting Workflow
                  </Typography>

                  <Stack spacing={0.6}>
                    {[
                      ["1", "Response", ActionTargetSubVariables?.TargetSubVariable1 === "net"
                        ? "Net"
                        : (colorLabels?.[ActionTargetSubVariables?.TargetSubVariable1] || ActionTargetSubVariables?.TargetSubVariable1 || "Select a response")],
                      ["2", "Scope", targetScopeLabel],
                      ["3", "See strongest subgroups", null],
                      ["4", "Compute strongest combined audience", null],
                    ].map(([step, label, value]) => (
                      <Stack key={step} direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: T.accentDim, border: `1px solid ${T.chipActiveBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "1px" }}>
                          <Typography sx={{ fontSize: "9px", fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{step}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                          {label}{value ? <Box component="span" sx={{ fontWeight: 700, color: T.text }}> {value}</Box> : null}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {audienceInsight?.perDimension && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 0.75 }}>
                        Top Group By Demographic
                      </Typography>
                      {Object.entries(audienceInsight.perDimension).map(([dim, info]) => (
                        <Stack key={dim} direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                          <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                            {dim.charAt(0).toUpperCase() + dim.slice(1)}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: T.text, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                            {info.label} <Box component="span" sx={{ color: T.accent }}>({info.score.toFixed(1)}%)</Box>
                          </Typography>
                        </Stack>
                      ))}
                      {/* Names what the share floor removed, so a missing group
                          reads as excluded rather than as absent data. */}
                      {audienceInsight.minSharePct != null && (
                        <Typography sx={{ fontSize: "10px", color: T.textFaint, mt: 0.75, lineHeight: 1.4, fontFamily: "'DM Sans', sans-serif" }}>
                          {audienceInsight.excludedSmall?.length
                            ? `Groups under ${audienceInsight.minSharePct}% of an area are excluded: ${audienceInsight.excludedSmall.join(", ")}.`
                            : `Groups under ${audienceInsight.minSharePct}% of an area are excluded.`}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {audienceInsight?.perDimension && (() => {
                    const dims = Object.entries(audienceInsight.perDimension);
                    const qualifyLabel = (dim, label) => dim === "vote24" ? `2024 ${label} voters` : dim === "party" ? `${label} voters` : label;
                    // Richer phrasing for use inside sentences
                    const describeLabel = (dim, label) => {
                      if (dim === "vote24") {
                        if (label === "Didn't vote") return "2024 non-voters";
                        if (label === "Other") return "2024 third-party voters";
                        return `2024 ${label} voters`;
                      }
                      if (dim === "party") return `${label} voters`;
                      if (dim === "ethnicity") return `${label} respondents`;
                      if (dim === "age") return `${label}-year-olds`;
                      if (dim === "gender") return label === "Men" || label === "Women" ? label.toLowerCase() : label;
                      if (dim === "education") return label.toLowerCase().includes("degree") && !label.toLowerCase().startsWith("no") ? "degree-educated respondents" : "respondents without a degree";
                      return label;
                    };
                    const personaLine = dims.map(([dim, info]) => qualifyLabel(dim, info.label)).join(", ");
                    const topDim = [...dims].sort((a, b) => b[1].score - a[1].score)[0];
                    const scope = targetScopeLabel !== "National" ? `in ${targetScopeLabel}` : "nationally";
                    const responseStr = audienceInsight.responseLabel || "this response";
                    const topPair = audienceInsight.topPairs?.[0];
                    const topPairDesc = topPair
                      ? topPair.label.split(" + ").map((part) => {
                          const matchingDim = dims.find(([, info]) => info.label === part.trim());
                          return matchingDim ? describeLabel(matchingDim[0], part.trim()) : part.trim();
                        }).join(" + ")
                      : null;
                    const topDimDesc = topDim ? describeLabel(topDim[0], topDim[1].label) : null;
                    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
                    // Build rich JSX sentences with bolding
                    const boldVal = (val) => <Box component="span" sx={{ fontWeight: 700, color: T.text }}>{val}</Box>;
                    const richSentences = [
                      <>{cap(topDimDesc || "This group")} {scope} show the strongest individual support at {boldVal(`${topDim[1].score.toFixed(1)}%`)} for {boldVal(responseStr)}.</>,
                      topPairDesc ? <>The highest-performing combined audience is {boldVal(topPairDesc)}, reaching {boldVal(`${topPair.score.toFixed(1)}%`)} support.</> : null,
                      <>This audience represents the strongest starting point for targeted outreach {scope}.</>,
                    ].filter(Boolean);
                    return (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 0.75 }}>
                          Suggested Persona
                        </Typography>
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif", mb: 0.75, letterSpacing: "0.01em" }}>
                          {dims.map(([dim, info]) => qualifyLabel(dim, info.label)).join(" · ")}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7 }}>
                          {richSentences.map((s, i) => (
                            <Box key={i} component="span">{s}{i < richSentences.length - 1 ? " " : ""}</Box>
                          ))}
                        </Typography>
                      </Box>
                    );
                  })()}

                  {audienceInsight?.topPairs?.length ? (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 0.75 }}>
                        Top Combined Audiences
                      </Typography>
                      {audienceInsight.topPairs.map((p, idx) => (
                        <Stack key={`${p.label}-${idx}`} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, fontFamily: "'DM Sans', sans-serif", minWidth: 16 }}>{idx + 1}</Typography>
                          <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif", flex: 1 }}>{p.label}</Typography>
                          <Typography sx={{ fontSize: "12px", color: T.accent, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>{p.score.toFixed(1)}%</Typography>
                        </Stack>
                      ))}
                    </Box>
                  ) : audienceInsight?.combined ? (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                        Strongest combined: <Box component="span" sx={{ color: T.text, fontWeight: 600 }}>{audienceInsight.combined.label}</Box>
                        <Box component="span" sx={{ color: T.accent }}> ({audienceInsight.combined.score.toFixed(1)}%)</Box>
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "12px", color: T.textFaint, fontStyle: "italic", fontFamily: "'DM Sans', sans-serif" }}>
                        Use this to identify the most supportive subgroup for outreach.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Compute Button */}
                <ButtonBase
                  onClick={() => onAudienceInsightRequest?.()}
                  disabled={!!audienceInsightLoading}
                  sx={{
                    width: "100%",
                    py: 1.1,
                    borderRadius: "9px",
                    background: audienceInsightLoading ? T.chip : `linear-gradient(135deg, #3b6fd4, #4f8ef7)`,
                    color: audienceInsightLoading ? T.textFaint : "#fff",
                    fontSize: "13px",
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: "0.01em",
                    boxShadow: audienceInsightLoading ? "none" : "0 4px 16px rgba(37,99,235,0.25)",
                    transition: "all 0.2s ease",
                    "&:hover:not(:disabled)": {
                      background: `linear-gradient(135deg, #2d5bb5, #4080e8)`,
                      boxShadow: "0 6px 20px rgba(37,99,235,0.35)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": { transform: "translateY(0)" },
                    cursor: audienceInsightLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {audienceInsightLoading ? "Computing…" : "Compute Top Audience"}
                </ButtonBase>

                {audienceInsightLoading && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress sx={{
                      height: 3,
                      borderRadius: 999,
                      bgcolor: T.chip,
                      "& .MuiLinearProgress-bar": { bgcolor: T.accent, borderRadius: 999 },
                    }} />
                  </Box>
                )}

                {/* Demographics Filter Grid */}
                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <DemographicGroup icon={PersonIcon} label="Gender" iconColor="#60a5fa">
                        {[[1, "Men"], [2, "Women"]].map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={genderFilter === val}
                            onClick={() => onGenderChange?.(toggleVal(genderFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      <DemographicGroup icon={CakeIcon} label="Age" iconColor="#a78bfa">
                        {[[1, "18–34"], [2, "35–64"], [3, "65+"]].map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={ageFilter === val}
                            onClick={() => onAgeChange?.(toggleVal(ageFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                  </Stack>
                  <DemographicGroup icon={SchoolIcon} label="Education" iconColor="#fbbf24">
                    {[[1, "No degree"], [2, "Degree"]].map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={educationFilter === val}
                        onClick={() => onEducationChange?.(toggleVal(educationFilter, val))} />
                    ))}
                  </DemographicGroup>
         <DemographicGroup icon={GroupsIcon} label="Ethnicity" iconColor="#34d399">
              {[[1, "White"], [2, "Black"], [3, "Hispanic"], [4, "Asian"], [5, "Other"]].map(([val, lbl]) => (
                <FilterChip
                  key={val}
                  label={lbl}
                  active={ethnicityFilter === val}
                  disabled={demo}
                  onClick={() => onEthnicityChange?.(toggleVal(ethnicityFilter, val))}
                />
              ))}
                    {demo && (
                      <Typography
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          fontSize: "10px",
                          color: T.textFaint,
                          fontStyle: "italic",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Available in full version
                      </Typography>
                    )}
            </DemographicGroup>

            <DemographicGroup icon={HowToVoteIcon} label="Party Registration" iconColor="#f87171">
              {[[1, "Democrat"], [2, "Republican"], [3, "Independent"], [4, "None"]].map(([val, lbl]) => (
                <FilterChip
                  key={val}
                  label={lbl}
                  active={partyFilter === val}
                  disabled={demo}
                  onClick={() => onPartyChange?.(toggleVal(partyFilter, val))}
                />
              ))}
                    {demo && (
                      <Typography
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          fontSize: "10px",
                          color: T.textFaint,
                          fontStyle: "italic",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Available in full version
                      </Typography>
                    )}
            </DemographicGroup>

                              <DemographicGroup icon={HowToVoteIcon} label="2024 Presidential Vote" iconColor="#800080">
                    {[[1, "Trump"], [2, "Harris"], [3, "Other"], [4, "Did Not Vote"]].map(([val, lbl]) => (
                      <FilterChip
                        key={val}
                        label={lbl}
                        active={vote24Filter === val}
                        disabled={demo}
                        onClick={() => onVote24Change?.(toggleVal(vote24Filter, val))}
                      />
                    ))}
                    {demo && (
                      <Typography
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          fontSize: "10px",
                          color: T.textFaint,
                          fontStyle: "italic",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Available in full version
                      </Typography>
                    )}
                  </DemographicGroup>

            
                </Stack>

                {onClearAll && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <ButtonBase
                      onClick={onClearAll}
                      sx={{
                        fontSize: "11px",
                        color: T.textFaint,
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        letterSpacing: "0.03em",
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "6px",
                        border: `1px solid ${T.border}`,
                        transition: "all 0.15s",
                        "&:hover": { color: T.text, borderColor: T.borderStrong, bgcolor: T.surfaceHover },
                      }}
                    >
                      Clear all
                    </ButtonBase>
                  </Box>
                )}
              </AccordionDetails>
            </StyledAccordion>

            {/* Key Area Statistics */}
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Key Area Statistics</SectionLabel>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <KeyInfo
                  hoverArea={hoverArea}
                  poliInfo={poliInfo}
                  geoLevel={geoLevel}
                  selectedAreaId={selectedFeature?.value ?? null}
                  selectedRowMatchIds={selectedFeature?.rowMatchIds}
                  selectedFeature={selectedFeature}
                  poliInfoToplevel={poliInfoToplevel}
                />
              </AccordionDetails>
            </StyledAccordion>
          </Box>
        )}

        {/* ── Chat Tab ── */}
        {activeTab === "chat" && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: T.bg }}>
            {/* Highlight active banner */}
            {chatHighlightCount > 0 && (
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 1.75, py: 0.75,
                bgcolor: T.activeTab,
                borderBottom: `1px solid ${T.chipActiveBorder}`,
                flexShrink: 0,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: T.accent }} />
                  <Typography sx={{ fontSize: 11.5, color: T.accent, fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
                    {chatHighlightCount} area{chatHighlightCount !== 1 ? "s" : ""} highlighted on map
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={onClearHighlights}
                  sx={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 11, color: T.textMuted, px: 0.75, py: 0.25, borderRadius: 1,
                    fontFamily: "'DM Sans', sans-serif",
                    "&:hover": { color: T.text },
                  }}
                >
                  Clear ✕
                </Box>
              </Box>
            )}

            {/* Messages */}
            <Box sx={{
              flex: 1, overflowY: "auto", px: 2, py: 1.5,
              display: "flex", flexDirection: "column", gap: 1.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
            }}>
              {chatMessages.map((msg, i) => {
                const isUser = msg.role === 'user';
                const isErr = msg.isError;
                return (
                  <Box key={i} sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
                    <Box sx={{
                      maxWidth: "85%", px: 1.5, py: 1,
                      borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                      bgcolor: isUser ? T.accent : isErr ? "rgba(220,38,38,0.05)" : T.surface,
                      color: isUser ? "#fff" : isErr ? "#b91c1c" : T.text,
                      fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap",
                      boxShadow: isUser ? "none" : `0 1px 3px rgba(0,0,0,0.06)`,
                      border: isUser ? "none" : isErr ? "1px solid rgba(220,38,38,0.2)" : `1px solid ${T.border}`,
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      {msg.content.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
                        part.startsWith('**') && part.endsWith('**')
                          ? <strong key={j}>{part.slice(2, -2)}</strong>
                          : part
                      )}

                      {/* Sample note caveat */}
                      {!isUser && msg.sampleNote && (
                        <Box sx={{
                          mt: 0.75, px: 1, py: 0.5, borderRadius: 1,
                          bgcolor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)",
                          fontSize: 11, color: "#92400e", fontFamily: "'DM Sans', sans-serif",
                        }}>
                          {msg.sampleNote}
                        </Box>
                      )}

                      {/* Retry on error */}
                      {isErr && msg.retryText && (
                        <Box
                          component="button"
                          onClick={() => handleChatSend(msg.retryText)}
                          sx={{
                            mt: 0.75, display: "block", background: "none", border: "none",
                            cursor: "pointer", fontSize: 11, color: "#b91c1c",
                            textDecoration: "underline", fontFamily: "'DM Sans', sans-serif",
                            "&:hover": { color: "#7f1d1d" },
                          }}
                        >
                          Try again
                        </Box>
                      )}

                      {/* Source disclosure */}
                      {!isUser && !isErr && msg.queryUsed && (
                        <Box sx={{ mt: 0.5 }}>
                          <Box
                            onClick={() => setChatQueryOpen(chatQueryOpen === i ? null : i)}
                            sx={{
                              display: "inline-flex", alignItems: "center", gap: 0.5,
                              cursor: "pointer", color: T.textFaint, fontSize: 11,
                              userSelect: "none", "&:hover": { color: T.textMuted },
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            {chatQueryOpen === i ? "Hide source" : "What I looked up"}
                          </Box>
                          <Collapse in={chatQueryOpen === i}>
                            <Box sx={{
                              mt: 0.5, p: 1, borderRadius: 1,
                              bgcolor: T.accentGlow, border: `1px solid ${T.border}`,
                              fontSize: 11, color: T.textMuted, fontFamily: "monospace", wordBreak: "break-all",
                            }}>
                              {msg.queryUsed.tool && <div><strong>tool:</strong> {msg.queryUsed.tool}</div>}
                              {msg.queryUsed.metric && <div><strong>metric:</strong> {msg.queryUsed.metric}</div>}
                              {msg.queryUsed.level && <div><strong>level:</strong> {msg.queryUsed.level}</div>}
                              {msg.queryUsed.state_codes?.length > 0 && (
                                <div><strong>states:</strong> {msg.queryUsed.state_codes.join(', ')}</div>
                              )}
                              {msg.queryUsed.filters && Object.keys(msg.queryUsed.filters).length > 0 && (
                                <div><strong>filters:</strong> {JSON.stringify(msg.queryUsed.filters)}</div>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
              {chatLoading && (
                <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: T.accent }} />
                  <Typography sx={{ fontSize: 11.5, color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                    Looking up data…
                  </Typography>
                </Box>
              )}
              <div ref={chatBottomRef} />
            </Box>

            {/* Input */}
            <Box sx={{ px: 1.5, pb: 2, pt: 1, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <TextField
                  multiline maxRows={4} value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                  placeholder="Ask about the data…" variant="outlined" size="small" fullWidth disabled={chatLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: T.surface, color: T.text, fontSize: 13, borderRadius: 2, fontFamily: "'DM Sans', sans-serif",
                      "& fieldset": { borderColor: T.border },
                      "&:hover fieldset": { borderColor: T.borderStrong },
                      "&.Mui-focused fieldset": { borderColor: T.accent },
                    },
                    "& .MuiInputBase-input::placeholder": { color: T.textFaint, opacity: 1 },
                  }}
                />
                <IconButton
                  onClick={() => handleChatSend()}
                  disabled={!chatInput.trim() || chatLoading}
                  sx={{ color: chatInput.trim() && !chatLoading ? T.accent : T.textFaint, p: 1 }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: 10, color: T.textFaint, mt: 0.75, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }}>
                Survey data only · Not a prediction tool
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Toggle Handle ── */}
      <Tooltip title={isPanelOpen ? "Collapse panel" : "Expand panel"} placement="right" arrow>
        <Box
          role="button"
          tabIndex={0}
          aria-label={isPanelOpen ? "Hide panel" : "Show panel"}
          onClick={togglePanel}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && togglePanel()}
          sx={{
            position: "relative",
            height: "100%",
            width: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: T.surface,
            borderLeft: `1px solid ${T.border}`,
            boxShadow: "4px 0 12px rgba(0,0,0,0.06)",
            cursor: "pointer",
            zIndex: 2000,
            transition: "background 0.15s",
            "&:hover": { bgcolor: T.surfaceHover },
          }}
        >
          <Box sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: T.chip,
            border: `1px solid ${T.borderStrong}`,
          }}>
            {isPanelOpen
              ? <ChevronRightIcon sx={{ fontSize: 14, color: T.textFaint }} />
              : <ChevronLeftIcon sx={{ fontSize: 14, color: T.textFaint }} />
            }
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default Sidebar;
